import { useState, useEffect, useRef, Fragment } from "react";
import { createPortal } from "react-dom";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAdminStats, useAdminUsers, useAdminCourses, useSupportCourses, useToggleSupportCourse, useAdminTickets, useReplyTicket, useSupportFaqs, useCreateSupportFaq, useUpdateSupportFaq, useDeleteSupportFaq, useTriageTicket, useGrantTicketCoins, useTicketStatus, useTicketNotes, useAdjustWallet, useBusinessOverview, useAdminBulkJobs, useAdminPublicPlaylist, useAddToPlaylist, useRemoveFromPlaylist, useReorderPlaylist, useFeaturedGenerate, useAlerts, useResolveAlert, useTransactions, useRefund, useUserDetail, useUpdateUser, useDeleteUser, useResetPassword, useModerationLog, useDeleteCourse, useNotifyUser, useNotifyAll, useCommTemplates, useCreateTemplate, useDeleteTemplate, useCommLog, useAdminFeedback, useResolveFeedback, useReplyFeedback, useInviteTester, useUpdateFeedback, useRejectFeedback, useRestoreFeedback, useFeedbackDigest, useWipLimits, useUpdateWipLimits } from "@/hooks/useAdmin";
import { useAdminSettings, useUpdateSettings, usePublicSettings, useUpdateLanding, useBranding, useBrandingMutation, useDojoAdmin, useDojoMutation, useGuideAdmin, useGuideMutation, useExpertMeAiAdmin, useUpdateExpertMeAi, useAdminPromos, useUpdatePromos } from "@/hooks/useSettings";
import { settingsService } from "@/services/settingsService";
import { adminService } from "@/services/adminService";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Users, BookOpen, Crown, DollarSign, Ticket, Coins, SlidersHorizontal, TrendingUp, AlertTriangle, CheckCircle2, Clock, Loader2, XCircle, Layers, Plus, Trash2, ArrowLeft, ListChecks, ChevronUp, ChevronDown, ChevronRight, Eye, Sparkles, Receipt, ShieldAlert, RotateCcw, Ban, ShieldCheck, UserCog, Upload, GraduationCap, Music, Landmark, ArrowRight, MessageSquare, Send, Flame, Megaphone, MessageCircleQuestion, FlaskConical, Bug, Lightbulb, Image as ImageIcon, Save, Rocket, KeyRound, Zap, Copy, Check, Database, LifeBuoy } from "lucide-react";

const COST_LABELS = { overview: "2-Minute Overview", lunch: "15-Minute Lunch Course", comprehensive: "Comprehensive Training" };

const JOB_STATUS = {
  completed: { icon: CheckCircle2, cls: "text-primary" },
  failed: { icon: XCircle, cls: "text-destructive" },
  running: { icon: Loader2, cls: "text-primary animate-spin" },
  pending: { icon: Clock, cls: "text-muted-foreground" },
};

function BulkJobs() {
  const { data: jobs = [] } = useAdminBulkJobs();
  return (
    <div className="border border-border bg-card overflow-x-auto" data-testid="admin-bulk-jobs">
      {jobs.length === 0 ? (
        <p className="text-muted-foreground text-sm p-5">No bulk generation jobs yet.</p>
      ) : (
        <Table>
          <TableHeader><TableRow><TableHead>Organization</TableHead><TableHead>Mode</TableHead><TableHead>Docs</TableHead><TableHead>Progress</TableHead><TableHead>Status</TableHead><TableHead>By</TableHead></TableRow></TableHeader>
          <TableBody>
            {jobs.map((j) => {
              const st = JOB_STATUS[j.status] || JOB_STATUS.pending;
              const Icon = st.icon;
              const pct = j.total ? Math.round((j.progress / j.total) * 100) : 0;
              return (
                <TableRow key={j.id} data-testid={`bulk-job-${j.id}`}>
                  <TableCell className="font-medium">{j.org_name}</TableCell>
                  <TableCell className="capitalize">{j.mode}{j.topic ? ` · ${j.topic}` : ""}</TableCell>
                  <TableCell>{j.doc_count}</TableCell>
                  <TableCell>
                    <div className="w-28">
                      <div className="h-1.5 bg-secondary"><div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} /></div>
                      <div className="text-[10px] text-muted-foreground mt-1">{j.progress}/{j.total}</div>
                    </div>
                  </TableCell>
                  <TableCell><span className={`inline-flex items-center gap-1 text-xs ${st.cls}`}><Icon className="h-3.5 w-3.5" /> {j.status}</span></TableCell>
                  <TableCell className="text-xs text-muted-foreground">{j.created_by}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="border border-border bg-card p-6" data-testid={`stat-${label}`}>
      <Icon className="h-5 w-5 text-primary mb-3" />
      <div className="font-display text-3xl font-extrabold">{value}</div>
      <div className="text-xs uppercase tracking-wider text-muted-foreground mt-1">{label}</div>
    </div>
  );
}

function fmtDuration(seconds) {
  if (seconds == null) return "—";
  const s = Math.round(seconds);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
  return `${Math.floor(s / 86400)}d ${Math.floor((s % 86400) / 3600)}h`;
}

export function PricingSettings() {
  const { data: settings } = useAdminSettings();
  const [ratio, setRatio] = useState("");
  const [costs, setCosts] = useState({});
  const [bounty, setBounty] = useState("");
  const [qa, setQa] = useState(true);
  const [qaSaving, setQaSaving] = useState(false);

  useEffect(() => {
    if (settings) {
      setRatio(String(settings.coin_usd_ratio));
      setCosts(settings.service_costs || {});
      setBounty(String(settings.feature_bounty_coins ?? 50));
      setQa(settings.qa_enabled !== false);
    }
  }, [settings]);

  const toggleQa = async (val) => {
    setQa(val);
    setQaSaving(true);
    try {
      await adminService.setQaEnabled(val);
      toast.success(`Post-course Q&A ${val ? "enabled" : "disabled"}`);
    } catch (e) {
      setQa(!val);
      toast.error(apiError(e));
    } finally {
      setQaSaving(false);
    }
  };

  const update = useUpdateSettings({
    onSuccess: () => toast.success("Pricing settings saved"),
    onError: (e) => toast.error(apiError(e)),
  });

  const save = () => {
    const r = parseFloat(ratio);
    if (!r || r <= 0) { toast.error("Coin ratio must be greater than 0"); return; }
    const service_costs = Object.fromEntries(Object.entries(costs).map(([k, v]) => [k, parseInt(v, 10) || 0]));
    update.mutate({ coin_usd_ratio: r, service_costs, feature_bounty_coins: parseInt(bounty, 10) || 0 });
  };

  const perCoin = ratio && parseFloat(ratio) > 0 ? (1 / parseFloat(ratio)).toFixed(2) : "—";

  return (
    <div className="max-w-xl space-y-8" data-testid="pricing-settings">
      <div className="border border-border bg-card p-6">
        <div className="flex items-center gap-2 mb-1"><Coins className="h-5 w-5 text-primary" /><h3 className="font-display font-bold">Coin ratio</h3></div>
        <p className="text-sm text-muted-foreground mb-4">Coins per US dollar. Each coin is worth <span className="font-semibold text-foreground">${perCoin}</span>.</p>
        <Label className="text-xs uppercase tracking-wider mb-2 block">Coins per $1 USD</Label>
        <Input type="number" min="1" step="0.5" value={ratio} onChange={(e) => setRatio(e.target.value)}
          className="rounded-none h-11 max-w-[160px]" data-testid="coin-ratio-input" />
      </div>

      <div className="border border-border bg-card p-6">
        <div className="flex items-center gap-2 mb-4"><SlidersHorizontal className="h-5 w-5 text-primary" /><h3 className="font-display font-bold">Service costs (coins)</h3></div>
        <div className="space-y-4">
          {["overview", "lunch", "comprehensive"].map((k) => (
            <div key={k} className="flex items-center justify-between gap-4">
              <Label className="text-sm">{COST_LABELS[k]}</Label>
              <Input type="number" min="0" value={costs[k] ?? ""} onChange={(e) => setCosts((c) => ({ ...c, [k]: e.target.value }))}
                className="rounded-none h-11 max-w-[120px]" data-testid={`cost-${k}-input`} />
            </div>
          ))}
        </div>
      </div>

      <div className="border border-border bg-card p-6">
        <div className="flex items-center gap-2 mb-1"><Sparkles className="h-5 w-5 text-primary" /><h3 className="font-display font-bold">Featured course bounty</h3></div>
        <p className="text-sm text-muted-foreground mb-4">Coins credited to an author the first time you feature their course in Watch &amp; Learn Free.</p>
        <Label className="text-xs uppercase tracking-wider mb-2 block">Coins per featured course</Label>
        <Input type="number" min="0" value={bounty} onChange={(e) => setBounty(e.target.value)}
          className="rounded-none h-11 max-w-[160px]" data-testid="feature-bounty-input" />
      </div>

      <div className="border border-border bg-card p-6" data-testid="qa-settings">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1"><MessageCircleQuestion className="h-5 w-5 text-primary" /><h3 className="font-display font-bold">Post-course Q&amp;A</h3></div>
            <p className="text-sm text-muted-foreground">Add a "Questions?" slide at the end of every course so learners can ask the AI a follow-up (saved to their Personal FAQ).</p>
          </div>
          <Switch checked={qa} disabled={qaSaving} onCheckedChange={toggleQa} data-testid="qa-enabled-toggle" />
        </div>
      </div>

      <Button onClick={save} disabled={update.isPending} className="rounded-none h-11 px-8 bg-primary hover:opacity-90" data-testid="save-settings-button">
        {update.isPending ? "Saving..." : "Save settings"}
      </Button>
    </div>
  );
}

export function QuickAddCoins({ users }) {
  const [email, setEmail] = useState("");
  const [amount, setAmount] = useState("");
  const add = useAdjustWallet({
    onSuccess: (d, vars) => {
      const u = users.find((x) => x.id === vars.userId);
      toast.success(`Added ${vars.delta} coins to ${u?.email || "user"} → ${d.credit_balance} total`);
      setAmount("");
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const submit = () => {
    const u = users.find((x) => x.email.toLowerCase() === email.trim().toLowerCase());
    if (!u) { toast.error("No user found with that email"); return; }
    const amt = parseInt(amount, 10);
    if (!amt) { toast.error("Enter a coin amount (use a negative number to deduct)"); return; }
    add.mutate({ userId: u.id, delta: amt, reason: "admin quick add" });
  };

  return (
    <div className="border border-primary/40 bg-primary/5 p-6 mb-10" data-testid="quick-add-coins">
      <div className="flex items-center gap-2 mb-1"><Coins className="h-5 w-5 text-primary" /><h3 className="font-display font-bold">Quick add Learn Me Coins</h3></div>
      <p className="text-sm text-muted-foreground mb-4">Top up any account instantly. Use a negative amount to deduct.</p>
      <div className="flex flex-col sm:flex-row gap-2">
        <input list="admin-user-emails" value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="user@email.com" className="flex-1 border border-border h-11 px-3 bg-background text-sm" data-testid="quick-coins-email" />
        <datalist id="admin-user-emails">
          {users.map((u) => <option key={u.id} value={u.email} />)}
        </datalist>
        <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
          placeholder="Coins (e.g. 100)" className="rounded-none h-11 sm:w-44" data-testid="quick-coins-amount" />
        <Button onClick={submit} disabled={add.isPending} className="rounded-none h-11 px-6 bg-primary hover:opacity-90 gap-1" data-testid="quick-coins-add">
          {add.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add coins
        </Button>
      </div>
    </div>
  );
}

const PLAN_OPTIONS = [
  { key: "learner", label: "Learner" },
  { key: "scholar", label: "Scholar" },
  { key: "enterprise", label: "Enterprise" },
];

function UserDetailDialog({ userId, onClose }) {
  const { data, isLoading } = useUserDetail(userId);
  const { user: me, refresh } = useAuth();
  const update = useUpdateUser({
    onSuccess: (res, vars) => {
      toast.success("User updated");
      if (me && vars?.id === me.id) refresh();
    },
    onError: (e) => toast.error(apiError(e)),
  });
  const invite = useInviteTester({
    onSuccess: (res, invitedId) => {
      toast.success("Invited to Beta — they've been notified");
      if (me && invitedId === me.id) refresh();
    },
    onError: (e) => toast.error(apiError(e)),
  });
  const del = useDeleteUser({
    onSuccess: () => { toast.success("User deleted"); onClose(); },
    onError: (e) => toast.error(apiError(e)),
  });
  const [showReset, setShowReset] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [copiedPw, setCopiedPw] = useState(false);
  const copyTempPassword = async () => {
    const val = newPassword.trim();
    if (!val) { toast.error("Nothing to copy yet"); return; }
    try {
      await navigator.clipboard.writeText(val);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = val; document.body.appendChild(ta); ta.select();
      document.execCommand("copy"); document.body.removeChild(ta);
    }
    setCopiedPw(true);
    toast.success("Temporary password copied");
    setTimeout(() => setCopiedPw(false), 1500);
  };
  const resetPw = useResetPassword({
    onSuccess: () => { toast.success("Password reset — share the new temporary password with the user"); setShowReset(false); setNewPassword(""); },
    onError: (e) => toast.error(apiError(e)),
  });
  const u = data?.user;
  return (
    <Dialog open={!!userId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="user-detail-dialog">
        <DialogHeader><DialogTitle>{u ? u.name : "User"}</DialogTitle></DialogHeader>
        {isLoading || !u ? (
          <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="space-y-5">
            <div className="text-sm text-muted-foreground">{u.email}</div>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="border border-border p-3"><div className="font-display text-xl font-bold">{data.stats.course_count}</div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Courses</div></div>
              <div className="border border-border p-3"><div className="font-display text-xl font-bold">{u.credit_balance}</div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Coins</div></div>
              <div className="border border-border p-3"><div className="font-display text-xl font-bold">${data.stats.total_spend}</div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Spend</div></div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider mb-2 block">Plan</Label>
              <div className="flex gap-1">
                {PLAN_OPTIONS.map((p) => (
                  <button key={p.key} onClick={() => update.mutate({ id: u.id, plan_type: p.key })} disabled={update.isPending}
                    data-testid={`user-plan-${p.key}`}
                    className={`px-3 h-9 text-xs font-semibold border ${u.plan_type === p.key ? "border-primary bg-primary text-white" : "border-border hover:border-foreground"}`}>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex gap-1">
                <button onClick={() => update.mutate({ id: u.id, role: u.role === "admin" ? "user" : "admin" })} disabled={update.isPending}
                  data-testid="user-toggle-role"
                  className="inline-flex items-center gap-1.5 px-3 h-9 text-xs font-semibold border border-border hover:border-foreground">
                  <UserCog className="h-3.5 w-3.5" /> {u.role === "admin" ? "Revoke admin" : "Make admin"}
                </button>
                <button onClick={() => update.mutate({ id: u.id, suspended: !u.suspended })} disabled={update.isPending}
                  data-testid="user-toggle-suspend"
                  className={`inline-flex items-center gap-1.5 px-3 h-9 text-xs font-semibold border ${u.suspended ? "border-emerald-500 text-emerald-600" : "border-amber-500 text-amber-600"}`}>
                  {u.suspended ? <><ShieldCheck className="h-3.5 w-3.5" /> Unsuspend</> : <><Ban className="h-3.5 w-3.5" /> Suspend</>}
                </button>
                <button onClick={() => u.is_tester ? update.mutate({ id: u.id, is_tester: false }) : invite.mutate(u.id)} disabled={update.isPending || invite.isPending}
                  data-testid="user-toggle-tester"
                  className={`inline-flex items-center gap-1.5 px-3 h-9 text-xs font-semibold border ${u.is_tester ? "border-indigo-500 text-indigo-600 bg-indigo-500/10" : "border-border hover:border-foreground"}`}>
                  <FlaskConical className="h-3.5 w-3.5" /> {u.is_tester ? "Remove tester" : "Invite as tester"}
                </button>
              </div>
              <button onClick={() => { if (window.confirm(`Delete ${u.email}? This cannot be undone.`)) del.mutate(u.id); }} disabled={del.isPending}
                data-testid="user-delete"
                className="inline-flex items-center gap-1.5 px-3 h-9 text-xs font-semibold border border-destructive text-destructive hover:bg-destructive hover:text-white transition-colors">
                <Trash2 className="h-3.5 w-3.5" /> Delete user
              </button>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider mb-2 block">Security</Label>
              {!showReset ? (
                <button onClick={() => setShowReset(true)} data-testid="user-reset-password-open"
                  className="inline-flex items-center gap-1.5 px-3 h-9 text-xs font-semibold border border-border hover:border-foreground">
                  <KeyRound className="h-3.5 w-3.5" /> Reset password
                </button>
              ) : (
                <div className="border border-border p-3 space-y-2" data-testid="user-reset-password-panel">
                  <p className="text-xs text-muted-foreground">Set a temporary password (min 8 characters). The user can change it after signing in.</p>
                  <div className="flex gap-2">
                    <PasswordInput value={newPassword} defaultVisible onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Temporary password" data-testid="user-reset-password-input"
                      toggleTestId="user-reset-password-toggle"
                      className="h-9 text-sm rounded-none" autoFocus />
                    <button onClick={copyTempPassword}
                      disabled={!newPassword.trim()}
                      title="Copy temporary password"
                      data-testid="user-reset-password-copy"
                      className="inline-flex items-center gap-1.5 px-3 h-9 text-xs font-semibold border border-border hover:border-foreground disabled:opacity-50 shrink-0">
                      {copiedPw ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
                      {copiedPw ? "Copied" : "Copy"}
                    </button>
                    <button onClick={() => resetPw.mutate({ id: u.id, new_password: newPassword })}
                      disabled={resetPw.isPending || newPassword.trim().length < 8}
                      data-testid="user-reset-password-save"
                      className="inline-flex items-center gap-1.5 px-3 h-9 text-xs font-semibold border border-primary bg-primary text-white disabled:opacity-50 shrink-0">
                      {resetPw.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
                    </button>
                    <button onClick={() => { setShowReset(false); setNewPassword(""); }}
                      data-testid="user-reset-password-cancel"
                      className="inline-flex items-center px-3 h-9 text-xs font-semibold border border-border hover:border-foreground shrink-0">
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider mb-2 block">Recent courses</Label>
              {data.courses.length === 0 ? (
                <p className="text-sm text-muted-foreground">No courses yet.</p>
              ) : (
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {data.courses.map((c) => (
                    <div key={c.id} className="flex items-center justify-between text-sm border border-border px-3 py-1.5">
                      <span className="truncate">{c.title}</span>
                      <span className="text-xs text-muted-foreground shrink-0 ml-2">{c.length_label}{c.is_public ? " · public" : ""}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function UsersPanel() {
  const { data: users = [] } = useAdminUsers();
  const [walletDelta, setWalletDelta] = useState({});
  const [detailId, setDetailId] = useState(null);
  const [q, setQ] = useState("");
  const [testersOnly, setTestersOnly] = useState(false);
  const [msgUser, setMsgUser] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const walletMutation = useAdjustWallet({
    onSuccess: (data, vars) => {
      toast.success(`Wallet updated → ${data.credit_balance} coins`);
      setWalletDelta((w) => ({ ...w, [vars.userId]: "" }));
    },
    onError: (e) => toast.error(apiError(e)),
  });
  const adjustWallet = (userId) => {
    const delta = parseInt(walletDelta[userId], 10);
    if (!delta) { toast.error("Enter a non-zero amount"); return; }
    walletMutation.mutate({ userId, delta, reason: "admin adjustment" });
  };
  const shown = users.filter((u) =>
    (!q || `${u.name} ${u.email}`.toLowerCase().includes(q.toLowerCase())) &&
    (!testersOnly || u.is_tester));
  const testerCount = users.filter((u) => u.is_tester).length;
  const fmtDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    const days = Math.floor((Date.now() - d.getTime()) / 86400000);
    if (days === 0) return "Today";
    if (days === 1) return "Yesterday";
    if (days < 30) return `${days}d ago`;
    return d.toLocaleDateString();
  };
  return (
    <div className="space-y-6" data-testid="admin-users-panel">
      <QuickAddCoins users={users} />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search users by name or email…"
          className="rounded-none h-10 max-w-sm" data-testid="user-search" />
        <div className="flex items-center gap-3">
          <button onClick={() => setTestersOnly((v) => !v)} data-testid="filter-testers-only"
            className={`inline-flex items-center gap-1.5 h-10 px-3 text-sm font-semibold border transition-colors ${testersOnly ? "border-indigo-500 bg-indigo-500/10 text-indigo-600" : "border-border hover:border-foreground"}`}>
            <FlaskConical className="h-4 w-4" /> Testers only
            <span className="text-xs font-bold bg-indigo-500/15 text-indigo-600 px-1.5 rounded-full">{testerCount}</span>
          </button>
          <span className="text-sm text-muted-foreground">{shown.length} of {users.length} users</span>
          <Button variant="outline" onClick={() => document.getElementById("communication-log")?.scrollIntoView({ behavior: "smooth", block: "start" })}
            className="rounded-none h-10 gap-2" data-testid="view-comm-log">
            <Receipt className="h-4 w-4" /> Communication Log
          </Button>
          <Button onClick={() => setBulkOpen(true)} className="rounded-none h-10 gap-2 bg-primary hover:opacity-90" data-testid="bulk-notify-open">
            <Megaphone className="h-4 w-4" /> Bulk Notification
          </Button>
        </div>
      </div>
      <div className="border border-border bg-card overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Beta</TableHead><TableHead>Coins</TableHead>
            <TableHead>Bites</TableHead><TableHead>Streak</TableHead><TableHead>Last Active</TableHead>
            <TableHead>Wallet</TableHead><TableHead className="text-right">Action</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {shown.map((u) => (
              <TableRow key={u.id} className={u.suspended ? "opacity-60" : ""}>
                <TableCell>
                  <div className="font-medium">{u.email}</div>
                  {u.name && <div className="text-xs text-muted-foreground">{u.name}</div>}
                </TableCell>
                <TableCell>{u.suspended ? <span className="text-amber-600 font-semibold">suspended</span> : (
                  <span className={u.role === "admin" ? "text-primary font-semibold" : ""}>{u.role === "admin" ? "admin" : (u.plan_label || u.plan_type)}</span>
                )}</TableCell>
                <TableCell data-testid={`user-tester-${u.id}`}>
                  {u.is_tester ? (
                    <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-indigo-500/10 text-indigo-600"><FlaskConical className="h-3 w-3" /> Beta</span>
                  ) : <span className="text-muted-foreground">—</span>}
                </TableCell>
                <TableCell data-testid={`user-coins-${u.id}`}>{u.credit_balance}</TableCell>
                <TableCell data-testid={`user-bites-${u.id}`}>{u.total_completed ?? 0}</TableCell>
                <TableCell data-testid={`user-streak-${u.id}`}>
                  {u.current_streak > 0 ? (
                    <span className="inline-flex items-center gap-1 font-semibold text-orange-600"><Flame className="h-3.5 w-3.5" /> {u.current_streak}d</span>
                  ) : <span className="text-muted-foreground">—</span>}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground" data-testid={`user-lastactive-${u.id}`}>{fmtDate(u.last_active)}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Input type="number" value={walletDelta[u.id] ?? ""} onChange={(e) => setWalletDelta((w) => ({ ...w, [u.id]: e.target.value }))}
                      placeholder="+/-" className="rounded-none h-9 w-20" data-testid={`panel-wallet-delta-${u.id}`} />
                    <Button size="sm" onClick={() => adjustWallet(u.id)} disabled={walletMutation.isPending}
                      className="rounded-none h-9 bg-primary hover:opacity-90" data-testid={`panel-wallet-apply-${u.id}`}>Apply</Button>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1 justify-end">
                    <Button size="sm" variant="outline" onClick={() => setMsgUser(u)} className="rounded-none h-9 gap-1" data-testid={`user-message-${u.id}`}>
                      <MessageSquare className="h-4 w-4" /> Message
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setDetailId(u.id)} className="rounded-none h-9" data-testid={`user-manage-${u.id}`}>Manage</Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <UserDetailDialog userId={detailId} onClose={() => setDetailId(null)} />
      <MessageUserDialog user={msgUser} onClose={() => setMsgUser(null)} />
      <BulkNotifyDialog open={bulkOpen} onClose={() => setBulkOpen(false)} count={users.length} />
      <CommunicationLog />
    </div>
  );
}

const TICKET_CATEGORIES = ["Coins", "Suggestion", "Stuck", "Billing"];
const TICKET_STATUSES = ["New", "Active - solution provided", "Active - Escalated", "Resolved"];
const STATUS_STYLES = {
  "New": "bg-blue-100 text-blue-700 border-blue-300",
  "Active - solution provided": "bg-amber-100 text-amber-700 border-amber-300",
  "Active - Escalated": "bg-red-100 text-red-700 border-red-300",
  "Resolved": "bg-green-100 text-green-700 border-green-300",
};

function TicketStatusSelect({ ticket }) {
  const status = ticket.status || "New";
  const mut = useTicketStatus({
    onSuccess: (d) => toast.success(`Status → ${d.status}`),
    onError: (e) => toast.error(apiError(e)),
  });
  return (
    <select value={status}
      onChange={(e) => mut.mutate({ id: ticket.id, status: e.target.value })}
      data-testid={`ticket-status-${ticket.id}`}
      className={`text-xs font-semibold px-2 py-1 border rounded-sm cursor-pointer ${STATUS_STYLES[status] || "bg-secondary border-border"}`}>
      {TICKET_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
    </select>
  );
}

function TicketNotes({ ticket }) {
  const [notes, setNotes] = useState(ticket.analyst_notes || "");
  const mut = useTicketNotes({
    onSuccess: () => toast.success("Notes saved"),
    onError: (e) => toast.error(apiError(e)),
  });
  const save = () => {
    if ((notes || "") === (ticket.analyst_notes || "")) return;
    mut.mutate({ id: ticket.id, analyst_notes: notes });
  };
  return (
    <div className="mt-4 border border-amber-500/30 bg-amber-500/5 p-4" data-testid={`ticket-notes-${ticket.id}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-amber-600" />
          <span className="text-xs font-bold uppercase tracking-wider text-amber-700">Support Notes</span>
          <span className="text-[11px] text-muted-foreground">(internal — not shown to customer)</span>
        </div>
        {ticket.analyst_notes_by && <span className="text-[11px] text-muted-foreground">last by {ticket.analyst_notes_by}</span>}
      </div>
      <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} onBlur={save}
        placeholder="Add internal notes for the support team…" className="rounded-none min-h-[70px] bg-background"
        data-testid={`ticket-notes-input-${ticket.id}`} />
    </div>
  );
}


function SupportFaqManager() {
  const { data: faqs = [] } = useSupportFaqs();
  const [newQ, setNewQ] = useState("");
  const [newCat, setNewCat] = useState(TICKET_CATEGORIES[0]);
  const [newA, setNewA] = useState("");
  const [editId, setEditId] = useState(null);
  const [editQ, setEditQ] = useState("");
  const [editCat, setEditCat] = useState("");
  const [editA, setEditA] = useState("");
  const create = useCreateSupportFaq({
    onSuccess: () => { toast.success("FAQ added"); setNewQ(""); setNewCat(TICKET_CATEGORIES[0]); setNewA(""); },
    onError: (e) => toast.error(apiError(e)),
  });
  const update = useUpdateSupportFaq({
    onSuccess: () => { toast.success("FAQ updated"); setEditId(null); },
    onError: (e) => toast.error(apiError(e)),
  });
  const del = useDeleteSupportFaq({
    onSuccess: () => toast.success("FAQ removed"),
    onError: (e) => toast.error(apiError(e)),
  });
  const startEdit = (f) => { setEditId(f.id); setEditQ(f.question); setEditCat(f.category); setEditA(f.answer || ""); };
  const submitNew = () => {
    if (!newQ.trim()) { toast.error("Enter a question"); return; }
    create.mutate({ question: newQ.trim(), category: newCat, answer: newA });
  };
  const submitEdit = (id) => {
    if (!editQ.trim()) { toast.error("Enter a question"); return; }
    update.mutate({ id, question: editQ.trim(), category: editCat, answer: editA });
  };
  return (
    <div className="border border-border bg-card p-5 mb-4" data-testid="support-faq-manager">
      <div className="flex items-center gap-2 mb-1">
        <MessageCircleQuestion className="h-4 w-4 text-primary" />
        <h3 className="font-display font-bold">Support FAQ Library</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-4">These entries power the “Applicable FAQ” dropdown on every ticket. The answer can be inserted straight into a reply.</p>
      <div className="border border-border p-3 mb-4 space-y-2">
        <div className="flex flex-col sm:flex-row gap-2">
          <Input value={newQ} onChange={(e) => setNewQ(e.target.value)} placeholder="New FAQ question…"
            className="rounded-none h-10 flex-1" data-testid="faq-new-question" />
          <select value={newCat} onChange={(e) => setNewCat(e.target.value)} data-testid="faq-new-category"
            className="h-10 px-3 border border-border bg-background text-sm sm:w-44">
            {TICKET_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <Textarea value={newA} onChange={(e) => setNewA(e.target.value)} placeholder="Answer (inserted into replies)…"
          className="rounded-none min-h-[70px]" data-testid="faq-new-answer" />
        <div className="flex justify-end">
          <Button onClick={submitNew} disabled={create.isPending} className="rounded-none h-10 gap-1 bg-primary hover:opacity-90" data-testid="faq-add-button">
            {create.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add FAQ
          </Button>
        </div>
      </div>
      <div className="border border-border divide-y divide-border" data-testid="faq-list">
        {faqs.length === 0 ? (
          <div className="p-4 text-sm text-muted-foreground">No FAQs yet — add one above.</div>
        ) : faqs.map((f) => (
          <div key={f.id} className="p-3" data-testid={`faq-row-${f.id}`}>
            {editId === f.id ? (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Input value={editQ} onChange={(e) => setEditQ(e.target.value)} className="rounded-none h-9 flex-1" data-testid={`faq-edit-question-${f.id}`} />
                  <select value={editCat} onChange={(e) => setEditCat(e.target.value)} data-testid={`faq-edit-category-${f.id}`}
                    className="h-9 px-2 border border-border bg-background text-sm w-36">
                    {TICKET_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <Textarea value={editA} onChange={(e) => setEditA(e.target.value)} placeholder="Answer…" className="rounded-none min-h-[70px]" data-testid={`faq-edit-answer-${f.id}`} />
                <div className="flex gap-2 justify-end">
                  <Button size="sm" variant="ghost" onClick={() => setEditId(null)} className="rounded-none h-9" data-testid={`faq-cancel-${f.id}`}>Cancel</Button>
                  <Button size="sm" onClick={() => submitEdit(f.id)} disabled={update.isPending} className="rounded-none h-9 gap-1 bg-primary hover:opacity-90" data-testid={`faq-save-${f.id}`}>
                    <Save className="h-3.5 w-3.5" /> Save
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <div className="text-sm font-medium">{f.question}</div>
                  {f.answer ? <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{f.answer}</div> : <div className="text-xs text-amber-600 mt-0.5">No answer set</div>}
                </div>
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-secondary shrink-0" data-testid={`faq-category-${f.id}`}>{f.category}</span>
                <button onClick={() => startEdit(f)} className="text-muted-foreground hover:text-foreground shrink-0" data-testid={`faq-edit-${f.id}`}><UserCog className="h-4 w-4" /></button>
                <button onClick={() => { if (window.confirm(`Delete this FAQ?`)) del.mutate(f.id); }} className="text-muted-foreground hover:text-destructive shrink-0" data-testid={`faq-delete-${f.id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function TicketTriagePanel({ ticket, faqs, courses }) {
  const [pain, setPain] = useState(ticket.customer_pain ?? null);
  const [category, setCategory] = useState(ticket.question_category ?? "");
  const [faqId, setFaqId] = useState(ticket.faq_id ?? "");
  const [courseId, setCourseId] = useState(ticket.course_id ?? "");
  const triage = useTriageTicket({
    onSuccess: () => toast.success("Triage saved"),
    onError: (e) => toast.error(apiError(e)),
  });
  const save = (next) => {
    const payload = {
      id: ticket.id,
      customer_pain: pain,
      question_category: category || null,
      faq_id: faqId || null,
      course_id: courseId || null,
      ...next,
    };
    triage.mutate(payload);
  };
  return (
    <div className="mt-4 border border-indigo-500/30 bg-indigo-500/5 p-4" data-testid={`ticket-triage-${ticket.id}`}>
      <div className="flex items-center gap-2 mb-3">
        <ListChecks className="h-4 w-4 text-indigo-600" />
        <span className="text-xs font-bold uppercase tracking-wider text-indigo-600">Triage</span>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5 block">Customer Pain</label>
          <div className="flex items-center gap-1" data-testid={`triage-pain-${ticket.id}`}>
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} type="button"
                onClick={() => { const v = pain === n ? null : n; setPain(v); save({ customer_pain: v }); }}
                data-testid={`triage-pain-${ticket.id}-${n}`}
                className={`h-9 w-9 text-sm font-bold border transition-colors ${pain === n ? "bg-indigo-600 text-white border-indigo-600" : "border-border hover:border-indigo-400"}`}>
                {n}
              </button>
            ))}
            <span className="ml-2 text-xs text-muted-foreground">1 = low · 5 = severe</span>
          </div>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5 block">Question Category</label>
          <select value={category}
            onChange={(e) => { setCategory(e.target.value); save({ question_category: e.target.value || null }); }}
            data-testid={`triage-category-${ticket.id}`}
            className="w-full h-10 px-3 border border-border bg-background text-sm">
            <option value="">— Select category —</option>
            {TICKET_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5 block">Applicable FAQ</label>
          <select value={faqId}
            onChange={(e) => { setFaqId(e.target.value); save({ faq_id: e.target.value || null }); }}
            data-testid={`triage-faq-${ticket.id}`}
            className="w-full h-10 px-3 border border-border bg-background text-sm">
            <option value="">— No FAQ —</option>
            {faqs.map((f) => <option key={f.id} value={f.id}>{f.question}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5 block">Applicable Support Course</label>
          <select value={courseId}
            onChange={(e) => { setCourseId(e.target.value); save({ course_id: e.target.value || null }); }}
            data-testid={`triage-course-${ticket.id}`}
            className="w-full h-10 px-3 border border-border bg-background text-sm">
            <option value="">— No course —</option>
            {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
          </select>
        </div>
      </div>
    </div>
  );
}

function SupportQuickCoins({ tickets }) {
  const submitters = Array.from(
    new Map(tickets.filter((t) => t.user_id).map((t) => [t.user_id, { id: t.user_id, email: t.user_email }])).values()
  );
  return (
    <div data-testid="support-quick-coins">
      <QuickAddCoins users={submitters} />
    </div>
  );
}

function GrantCoinsButton({ ticket }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("50");
  const userId = ticket.user_id;
  const grants = ticket.grants || [];
  const totalGranted = grants.reduce((s, g) => s + (g.delta || 0), 0);
  const grant = useGrantTicketCoins({
    onSuccess: (d) => { toast.success(`Granted ${amount} coins to ${ticket.user_email} → ${d.credit_balance} total`); setOpen(false); setAmount("50"); },
    onError: (e) => toast.error(apiError(e)),
  });
  const submit = () => {
    const amt = parseInt(amount, 10);
    if (!amt) { toast.error("Enter a coin amount"); return; }
    grant.mutate({ id: ticket.id, delta: amt });
  };
  if (!userId) return null;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" className="rounded-none h-8 gap-1.5 text-xs" data-testid={`grant-coins-btn-${ticket.id}`}>
          <Coins className="h-3.5 w-3.5" /> Grant coins
          {grants.length > 0 && <span className="ml-1 px-1.5 py-0.5 bg-primary text-primary-foreground text-[10px] font-bold rounded-full" data-testid={`grant-count-${ticket.id}`}>{grants.length}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 rounded-none" data-testid={`grant-coins-popover-${ticket.id}`}>
        {grants.length > 0 && (
          <div className="mb-3 pb-3 border-b border-border" data-testid={`grant-history-popover-${ticket.id}`}>
            <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Already granted · {totalGranted} total</p>
            <div className="space-y-1 max-h-24 overflow-y-auto">
              {grants.map((g, i) => (
                <div key={i} className="text-[11px] text-muted-foreground flex justify-between gap-2">
                  <span className="font-medium text-foreground">{g.delta > 0 ? `+${g.delta}` : g.delta}</span>
                  <span className="truncate">{g.by} · {new Date(g.at).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <p className="text-xs text-muted-foreground mb-2">Grant Learn Me Coins to <span className="font-medium text-foreground">{ticket.user_email}</span>. Use a negative amount to deduct.</p>
        <div className="grid grid-cols-3 gap-1">
          {[25, 50, 100].map((n) => (
            <button key={n} type="button" onClick={() => setAmount(String(n))}
              className={`h-8 text-xs border ${String(n) === amount ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`}
              data-testid={`grant-preset-${ticket.id}-${n}`}>{n}</button>
          ))}
        </div>
        <div className="flex gap-2 mt-2">
          <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="rounded-none h-9" data-testid={`grant-amount-${ticket.id}`} />
          <Button onClick={submit} disabled={grant.isPending} className="rounded-none h-9 gap-1 bg-primary hover:opacity-90" data-testid={`grant-confirm-${ticket.id}`}>
            {grant.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Grant
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function StatusBadge({ status }) {
  const s = status || "New";
  return <span className={`inline-block text-[11px] font-semibold px-2 py-0.5 border rounded-sm ${STATUS_STYLES[s] || "bg-secondary border-border"}`} data-testid="ticket-status-badge">{s}</span>;
}

function TicketListItem({ ticket: t, faqs, courses, expanded, onToggle }) {
  const [replyText, setReplyText] = useState("");
  const replyMutation = useReplyTicket({
    onSuccess: () => { toast.success("Reply sent"); setReplyText(""); },
    onError: (e) => toast.error(apiError(e)),
  });
  const sendReply = () => { if (!replyText.trim()) return; replyMutation.mutate({ id: t.id, message: replyText }); };
  const created = t.created_at ? new Date(t.created_at).toLocaleDateString() : "—";
  return (
    <div className="border border-border bg-card" data-testid={`support-ticket-${t.id}`}>
      <button type="button" onClick={onToggle}
        className="w-full grid grid-cols-12 gap-3 items-center px-4 py-3 text-left hover:bg-black/[0.02] transition-colors"
        data-testid={`ticket-row-${t.id}`}>
        <div className="col-span-4 flex items-center gap-2 min-w-0">
          <ChevronRight className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${expanded ? "rotate-90" : ""}`} />
          <span className="font-medium text-sm truncate">{t.subject}</span>
        </div>
        <div className="col-span-2"><StatusBadge status={t.status} /></div>
        <div className="col-span-3 min-w-0">
          <div className="text-xs font-medium truncate">{t.user_name}</div>
          <div className="text-[11px] text-muted-foreground truncate">{t.user_email}</div>
        </div>
        <div className="col-span-2 text-xs text-muted-foreground truncate">{t.last_agent || "—"}</div>
        <div className="col-span-1 text-xs text-muted-foreground text-right">{created}</div>
      </button>
      {expanded && (
        <div className="border-t border-border p-5" data-testid={`ticket-detail-${t.id}`}>
          <div className="flex justify-between items-start mb-3 gap-3">
            <GrantCoinsButton ticket={t} />
            <TicketStatusSelect ticket={t} />
          </div>
          {t.grants?.length > 0 && (
            <div className="mb-3 inline-flex items-center gap-1.5 text-xs px-2 py-1 bg-primary/10 text-primary border border-primary/20" data-testid={`grant-note-${t.id}`}>
              <Coins className="h-3.5 w-3.5" /> {t.grants.reduce((s, g) => s + (g.delta || 0), 0)} coins granted ({t.grants.length}× — last by {t.grants[t.grants.length - 1].by})
            </div>
          )}
          <p className="text-sm mb-3">{t.message}</p>
          {t.replies?.map((r, i) => (
            <div key={i} className="border-l-2 border-primary pl-3 mb-2 text-sm"><span className="text-xs font-semibold text-primary">You:</span> {r.message}</div>
          ))}
          <div className="flex gap-2 mt-2">
            <Input value={replyText} onChange={(e) => setReplyText(e.target.value)}
              placeholder="Write a reply..." className="rounded-none h-10" data-testid={`reply-input-${t.id}`} />
            <Button onClick={sendReply} className="rounded-none bg-primary hover:opacity-90" data-testid={`reply-button-${t.id}`}>Reply</Button>
          </div>
          {t.faq_answer && (
            <button type="button"
              onClick={() => setReplyText((prev) => prev ? `${prev}\n\n${t.faq_answer}` : t.faq_answer)}
              className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
              data-testid={`insert-faq-${t.id}`}>
              <MessageCircleQuestion className="h-3.5 w-3.5" /> Insert FAQ answer: “{t.faq_question}”
            </button>
          )}
          <TicketTriagePanel ticket={t} faqs={faqs} courses={courses} />
          <TicketNotes ticket={t} />
        </div>
      )}
    </div>
  );
}

export function SupportTicketsPanel({ filter = { category: null, pain: null }, onFilterChange }) {
  const { data: tickets = [] } = useAdminTickets();
  const { data: faqs = [] } = useSupportFaqs();
  const { data: courses = [] } = useSupportCourses();
  const [manageFaqs, setManageFaqs] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());
  const toggle = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const matchesFilter = (t) => {
    if (filter.category === "__untagged__" ? t.question_category : (filter.category && t.question_category !== filter.category)) return false;
    if (filter.pain && t.customer_pain !== filter.pain) return false;
    return true;
  };
  const filtered = tickets.filter(matchesFilter);
  const hasFilter = filter.category || filter.pain;
  const clearFilter = () => onFilterChange?.({ category: null, pain: null });
  const catLabel = filter.category === "__untagged__" ? "Untagged" : filter.category;
  return (
    <div className="space-y-4" data-testid="support-tickets-panel">
      <SupportQuickCoins tickets={tickets} />
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          {hasFilter && (
            <div className="flex items-center gap-2 text-sm" data-testid="support-active-filter">
              <span className="text-muted-foreground">Showing {filtered.length} of {tickets.length}:</span>
              {filter.category && <span className="px-2 py-0.5 bg-secondary text-xs font-medium">Category: {catLabel}</span>}
              {filter.pain && <span className="px-2 py-0.5 bg-secondary text-xs font-medium">Pain: {filter.pain}</span>}
              <button onClick={clearFilter} className="inline-flex items-center gap-1 text-xs text-primary hover:underline" data-testid="clear-support-filter">
                <XCircle className="h-3.5 w-3.5" /> Clear
              </button>
            </div>
          )}
        </div>
        <Button variant="outline" onClick={() => setManageFaqs((v) => !v)} className="rounded-none h-10 gap-2" data-testid="toggle-faq-manager">
          <MessageCircleQuestion className="h-4 w-4" /> {manageFaqs ? "Hide FAQ Library" : "Manage Support FAQs"}
        </Button>
      </div>
      {manageFaqs && <SupportFaqManager />}
      {filtered.length === 0 ? (
        <div className="border border-dashed border-border bg-card p-8 text-center text-sm text-muted-foreground" data-testid="support-tickets-empty">
          {hasFilter ? "No tickets match this filter." : "No support tickets yet."}
        </div>
      ) : (
        <div className="border border-border bg-card" data-testid="support-tickets-list">
          <div className="grid grid-cols-12 gap-3 px-4 py-2 border-b border-border bg-muted/40 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
            <div className="col-span-4">Title</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-3">Customer</div>
            <div className="col-span-2">Agent</div>
            <div className="col-span-1 text-right">Created</div>
          </div>
          <div className="divide-y divide-border">
            {filtered.map((t) => (
              <TicketListItem key={t.id} ticket={t} faqs={faqs} courses={courses}
                expanded={expanded.has(t.id)} onToggle={() => toggle(t.id)} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function CoursesPanel() {
  const { data: courses = [] } = useAdminCourses();
  const deleteCourse = useDeleteCourse({
    onSuccess: () => toast.success("Course deleted"),
    onError: (e) => toast.error(apiError(e)),
  });
  const toggleSupport = useToggleSupportCourse({
    onSuccess: (d) => toast.success(d.is_support_course ? "Added to support courses" : "Removed from support courses"),
    onError: (e) => toast.error(apiError(e)),
  });
  return (
    <div className="border border-border bg-card overflow-x-auto" data-testid="admin-courses-panel">
      <Table>
        <TableHeader><TableRow><TableHead>Title</TableHead><TableHead>Author</TableHead><TableHead>Format</TableHead><TableHead>Duration</TableHead><TableHead>Public</TableHead><TableHead>Support</TableHead><TableHead></TableHead></TableRow></TableHeader>
        <TableBody>
          {courses.map((c) => (
            <TableRow key={c.id}>
              <TableCell className="font-medium max-w-xs truncate">{c.title}</TableCell>
              <TableCell className="text-sm text-muted-foreground">{c.author}</TableCell>
              <TableCell>{c.length_label}</TableCell>
              <TableCell data-testid={`course-duration-${c.id}`} className="tabular-nums whitespace-nowrap">
                {(() => {
                  const s = c.duration_seconds || 0;
                  const t = `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
                  return c.duration_measured ? t : `~${t}`;
                })()}
                <span className="ml-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                  {c.duration_measured ? "actual" : "est"}
                </span>
              </TableCell>
              <TableCell>{c.is_public ? <span className="text-primary text-xs font-semibold">public</span> : <span className="text-xs text-muted-foreground">—</span>}</TableCell>
              <TableCell>
                <button
                  onClick={() => toggleSupport.mutate({ id: c.id, isSupport: !c.is_support_course })}
                  className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 border transition-colors ${c.is_support_course ? "bg-primary/10 text-primary border-primary/30" : "text-muted-foreground border-border hover:border-primary"}`}
                  data-testid={`course-support-toggle-${c.id}`}>
                  <LifeBuoy className="h-3.5 w-3.5" /> {c.is_support_course ? "Support" : "Mark support"}
                </button>
              </TableCell>
              <TableCell>
                <button onClick={() => { if (window.confirm(`Delete "${c.title}"? This cannot be undone.`)) deleteCourse.mutate(c.id); }}
                  className="text-muted-foreground hover:text-destructive" data-testid={`course-delete-${c.id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function CommunicationLog() {
  const { data: log = [] } = useCommLog();
  const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");
  return (
    <div id="communication-log" data-testid="communication-log" className="scroll-mt-24">
      <div className="flex items-center gap-2 mb-3 mt-2">
        <Megaphone className="h-4 w-4 text-primary" />
        <h3 className="font-display font-bold">Communication Log</h3>
        <span className="text-xs text-muted-foreground">— everything you've sent</span>
      </div>
      {log.length === 0 ? (
        <div className="border border-dashed border-border bg-card p-8 text-center text-sm text-muted-foreground" data-testid="comm-log-empty">
          No messages sent yet. Use “Message” on a user or “Bulk Notification” above.
        </div>
      ) : (
        <div className="border border-border bg-card overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Sent</TableHead><TableHead>Audience</TableHead><TableHead>Message</TableHead>
              <TableHead>Recipients</TableHead><TableHead>Read</TableHead><TableHead>By</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {log.map((l) => {
                const rate = l.recipient_count ? Math.round((l.read_count / l.recipient_count) * 100) : 0;
                return (
                  <TableRow key={l.id} data-testid={`comm-log-row-${l.id}`}>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{fmt(l.created_at)}</TableCell>
                    <TableCell className="whitespace-nowrap font-medium">{l.audience}</TableCell>
                    <TableCell className="max-w-md">
                      {l.title && <div className="font-semibold text-sm">{l.title}</div>}
                      <div className="text-sm text-muted-foreground line-clamp-2">{l.body}</div>
                    </TableCell>
                    <TableCell>{l.recipient_count}</TableCell>
                    <TableCell><span className="font-semibold">{l.read_count}</span> <span className="text-xs text-muted-foreground">({rate}%)</span></TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{l.sent_by}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function TemplateBar({ title, body, onApply }) {
  const { data: templates = [] } = useCommTemplates();
  const createT = useCreateTemplate({
    onSuccess: () => toast.success("Template saved"),
    onError: (e) => toast.error(apiError(e)),
  });
  const delT = useDeleteTemplate({ onError: (e) => toast.error(apiError(e)) });
  return (
    <div className="border border-border bg-secondary/40 p-3 space-y-2" data-testid="template-bar">
      <div className="flex items-center gap-2">
        <Select onValueChange={(id) => { const t = templates.find((x) => x.id === id); if (t) onApply(t.title, t.body); }}>
          <SelectTrigger className="rounded-none h-9 flex-1" data-testid="template-select">
            <SelectValue placeholder={templates.length ? "Load a template…" : "No templates yet"} />
          </SelectTrigger>
          <SelectContent>
            {templates.map((t) => (
              <SelectItem key={t.id} value={t.id} data-testid={`template-option-${t.id}`}>
                {t.title || t.body.slice(0, 40)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button type="button" size="sm" variant="outline" className="rounded-none h-9 gap-1"
          disabled={!body.trim() || createT.isPending}
          onClick={() => createT.mutate({ title, body })} data-testid="template-save">
          <Plus className="h-3.5 w-3.5" /> Save as template
        </Button>
      </div>
      {templates.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {templates.map((t) => (
            <span key={t.id} className="inline-flex items-center gap-1 text-[11px] border border-border px-2 py-0.5 bg-background">
              <button type="button" onClick={() => onApply(t.title, t.body)} className="hover:text-primary" data-testid={`template-chip-${t.id}`}>{t.title || t.body.slice(0, 24)}</button>
              <button type="button" onClick={() => delT.mutate(t.id)} className="text-muted-foreground hover:text-rose-600" data-testid={`template-delete-${t.id}`}><XCircle className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function MessageUserDialog({ user, onClose }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const notify = useNotifyUser({
    onSuccess: () => { toast.success(`Notification sent to ${user?.email}`); setTitle(""); setBody(""); onClose(); },
    onError: (e) => toast.error(apiError(e)),
  });
  const send = () => {
    if (!body.trim()) { toast.error("Write a message first"); return; }
    notify.mutate({ userId: user.id, title: title.trim(), body: body.trim() });
  };
  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none" data-testid="message-user-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><MessageSquare className="h-5 w-5 text-primary" /> Message {user?.email}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground -mt-2">Sends an in-app notification to this user's bell.</p>
        <div className="space-y-3">
          <TemplateBar title={title} body={body} onApply={(t, b) => { setTitle(t); setBody(b); }} />
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (optional)" className="rounded-none" data-testid="message-title" />
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} placeholder="Your message…"
            className="w-full border border-input bg-background px-3 py-2 text-sm rounded-none resize-none focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" data-testid="message-body" />
          <Button onClick={send} disabled={notify.isPending} className="w-full rounded-none gap-2 bg-primary hover:opacity-90" data-testid="message-send">
            {notify.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send notification
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BulkNotifyDialog({ open, onClose, count }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const notify = useNotifyAll({
    onSuccess: (d) => { toast.success(`Sent to ${d?.count ?? count} users`); setTitle(""); setBody(""); onClose(); },
    onError: (e) => toast.error(apiError(e)),
  });
  const send = () => {
    if (!body.trim()) { toast.error("Write a message first"); return; }
    notify.mutate({ title: title.trim(), body: body.trim() });
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none" data-testid="bulk-notify-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Megaphone className="h-5 w-5 text-primary" /> Bulk Notification</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground -mt-2">Sends an in-app notification to <b>all {count} users</b>.</p>
        <div className="space-y-3">
          <TemplateBar title={title} body={body} onApply={(t, b) => { setTitle(t); setBody(b); }} />
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (optional)" className="rounded-none" data-testid="bulk-title" />
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} placeholder="Announce something to everyone…"
            className="w-full border border-input bg-background px-3 py-2 text-sm rounded-none resize-none focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" data-testid="bulk-body" />
          <Button onClick={send} disabled={notify.isPending} className="w-full rounded-none gap-2 bg-primary hover:opacity-90" data-testid="bulk-send">
            {notify.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send to all users
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const FEATURE_LENGTHS = [
  { key: "overview", label: "2-min Overview" },
  { key: "lunch", label: "15-min Lunch" },
  { key: "comprehensive", label: "Comprehensive" },
];

export function PublicPlaylistManager() {
  const { data: playlist = [] } = useAdminPublicPlaylist();
  const { data: allCourses = [] } = useAdminCourses();
  const add = useAddToPlaylist({
    onSuccess: (data) => toast.success(data?.credited ? `Featured — author credited ${data.credited} coins` : "Now viewable in Watch & Learn Free"),
    onError: (e) => toast.error(apiError(e)),
  });
  const remove = useRemoveFromPlaylist({ onSuccess: () => toast.success("Removed from the public playlist"), onError: (e) => toast.error(apiError(e)) });
  const reorder = useReorderPlaylist({ onError: (e) => toast.error(apiError(e)) });
  const generate = useFeaturedGenerate({
    onSuccess: (data) => toast.success(`Generated & published: ${data.title}`),
    onError: (e) => toast.error(apiError(e)),
  });

  const [topic, setTopic] = useState("");
  const [length, setLength] = useState("overview");
  const [search, setSearch] = useState("");

  const publicIds = new Set(playlist.map((c) => c.id));
  const candidates = allCourses
    .filter((c) => !c.is_public && !publicIds.has(c.id))
    .filter((c) => !search || `${c.title} ${c.topic} ${c.author}`.toLowerCase().includes(search.toLowerCase()));

  const runGenerate = () => {
    if (!topic.trim()) { toast.error("Enter a topic to generate"); return; }
    generate.mutate({ topic: topic.trim(), length }, { onSuccess: () => setTopic("") });
  };

  const move = (index, dir) => {
    const j = index + dir;
    if (j < 0 || j >= playlist.length) return;
    const next = [...playlist];
    [next[index], next[j]] = [next[j], next[index]];
    reorder.mutate(next.map((c) => c.id));
  };

  return (
    <div className="space-y-6" data-testid="admin-playlist-manager">
      {/* Generator */}
      <div className="border border-border bg-card p-6" data-testid="featured-generator">
        <div className="flex items-center gap-2 mb-1"><Sparkles className="h-4 w-4 text-primary" /><h3 className="font-display font-bold">Generate a featured course</h3></div>
        <p className="text-xs text-muted-foreground mb-4">Create a fresh course under your admin account and publish it to the free playlist instantly.</p>
        <div className="flex flex-col md:flex-row gap-3">
          <Input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. How black holes bend time"
            className="rounded-none h-11 flex-1" data-testid="featured-topic-input"
            onKeyDown={(e) => e.key === "Enter" && !generate.isPending && runGenerate()} />
          <div className="flex gap-1">
            {FEATURE_LENGTHS.map((l) => (
              <button key={l.key} onClick={() => setLength(l.key)} data-testid={`featured-length-${l.key}`}
                className={`px-3 h-11 text-xs font-semibold border ${length === l.key ? "border-primary bg-primary text-white" : "border-border hover:border-foreground"}`}>
                {l.label}
              </button>
            ))}
          </div>
          <Button onClick={runGenerate} disabled={generate.isPending} className="rounded-none h-11 bg-primary hover:opacity-90 gap-2" data-testid="featured-generate-btn">
            {generate.isPending ? <><Loader2 className="h-4 w-4 animate-spin" /> Generating…</> : <><Sparkles className="h-4 w-4" /> Generate &amp; publish</>}
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="border border-border bg-card p-6">
          <div className="flex items-center gap-2 mb-1"><Eye className="h-4 w-4 text-primary" /><h3 className="font-display font-bold">Watch &amp; Learn Free playlist ({playlist.length})</h3></div>
          <p className="text-xs text-muted-foreground mb-4">Viewable on the landing/theater without login. Use the arrows to set the order they appear in.</p>
          {playlist.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">No public courses yet. Generate one above or add one from the right.</p>
          ) : (
            <div className="space-y-2" data-testid="admin-playlist-current">
              {playlist.map((c, i) => (
                <div key={c.id} className="flex items-center gap-2 border border-border p-3" data-testid={`playlist-current-${c.id}`}>
                  <span className="text-xs font-mono text-muted-foreground w-5 shrink-0">{i + 1}</span>
                  <div className="flex flex-col shrink-0">
                    <button onClick={() => move(i, -1)} disabled={i === 0 || reorder.isPending} className="text-muted-foreground hover:text-primary disabled:opacity-30" data-testid={`playlist-up-${c.id}`}>
                      <ChevronUp className="h-4 w-4" />
                    </button>
                    <button onClick={() => move(i, 1)} disabled={i === playlist.length - 1 || reorder.isPending} className="text-muted-foreground hover:text-primary disabled:opacity-30" data-testid={`playlist-down-${c.id}`}>
                      <ChevronDown className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm truncate">{c.title}</div>
                    <div className="text-xs text-muted-foreground">{c.length_label} · {c.section_count} chapters</div>
                  </div>
                  <button onClick={() => remove.mutate(c.id)} className="text-muted-foreground hover:text-destructive shrink-0" data-testid={`playlist-remove-${c.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border border-border bg-card p-6">
          <h3 className="font-display font-bold mb-1">Feature a member's course</h3>
          <p className="text-xs text-muted-foreground mb-3">Publish any user's course and the author is automatically credited coins as a thank-you.</p>
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search courses or authors…"
            className="rounded-none h-10 mb-3" data-testid="featured-candidate-search" />
          {candidates.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">No matching courses to feature.</p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="admin-playlist-candidates">
              {candidates.map((c) => (
                <div key={c.id} className="flex items-center justify-between gap-2 border border-border p-3" data-testid={`playlist-candidate-${c.id}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{c.title}</div>
                    <div className="text-xs text-muted-foreground truncate">{c.length_label} · by {c.author}</div>
                  </div>
                  <Button size="sm" onClick={() => add.mutate(c.id)} disabled={add.isPending} className="rounded-none bg-primary hover:opacity-90 gap-1 shrink-0" data-testid={`playlist-add-${c.id}`}>
                    <Plus className="h-4 w-4" /> Feature
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function renderEmphasis(text, cls) {
  return (text || "").split("**").map((seg, i) =>
    i % 2 === 1 ? <span key={i} className={cls}>{seg}</span> : <span key={i}>{seg}</span>
  );
}

export function LandingContentManager() {
  const { data: settings } = usePublicSettings();
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [searchPlaceholder, setSearchPlaceholder] = useState("");
  const [playlistHeading, setPlaylistHeading] = useState("");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (settings?.landing && !loaded) {
      setTitle(settings.landing.title || "");
      setSubtitle(settings.landing.subtitle || "");
      setSearchPlaceholder(settings.landing.search_placeholder || "");
      setPlaylistHeading(settings.landing.playlist_heading || "");
      setLoaded(true);
    }
  }, [settings, loaded]);

  const save = useUpdateLanding({
    onSuccess: () => toast.success("Landing content updated"),
    onError: (e) => toast.error(apiError(e)),
  });

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="admin-landing-manager">
      <div className="border border-border bg-card p-6">
        <h3 className="font-display font-bold mb-1">Landing hero content</h3>
        <p className="text-xs text-muted-foreground mb-4">
          Wrap words in <code className="bg-secondary px-1">**double asterisks**</code> to highlight them in bold blue.
        </p>
        <label className="text-xs uppercase tracking-wider font-bold text-muted-foreground block mb-1">Title</label>
        <textarea value={title} onChange={(e) => setTitle(e.target.value)} rows={3}
          className="w-full border border-border p-3 bg-background text-sm mb-4" data-testid="landing-title-input" />
        <label className="text-xs uppercase tracking-wider font-bold text-muted-foreground block mb-1">Subtitle</label>
        <textarea value={subtitle} onChange={(e) => setSubtitle(e.target.value)} rows={3}
          className="w-full border border-border p-3 bg-background text-sm mb-4" data-testid="landing-subtitle-input" />
        <label className="text-xs uppercase tracking-wider font-bold text-muted-foreground block mb-1">Search bar placeholder</label>
        <input value={searchPlaceholder} onChange={(e) => setSearchPlaceholder(e.target.value)}
          className="w-full border border-border p-3 bg-background text-sm mb-4 h-11" data-testid="landing-placeholder-input" />
        <label className="text-xs uppercase tracking-wider font-bold text-muted-foreground block mb-1">"Featured courses" heading</label>
        <input value={playlistHeading} onChange={(e) => setPlaylistHeading(e.target.value)}
          className="w-full border border-border p-3 bg-background text-sm mb-4 h-11" data-testid="landing-heading-input" />
        <Button onClick={() => save.mutate({ title: title.trim(), subtitle: subtitle.trim(), search_placeholder: searchPlaceholder.trim(), playlist_heading: playlistHeading.trim() })}
          disabled={save.isPending || !title.trim()} className="rounded-none bg-primary hover:opacity-90" data-testid="landing-save-button">
          {save.isPending ? "Saving..." : "Save content"}
        </Button>
      </div>

      <div className="border border-border bg-card p-6" data-testid="landing-preview">
        <p className="text-xs uppercase tracking-wider font-bold text-muted-foreground mb-4">Live preview</p>
        <h1 className="font-display text-3xl font-extrabold tracking-tighter leading-tight mb-4">
          {renderEmphasis(title, "text-primary")}
        </h1>
        <p className="text-lg text-muted-foreground leading-relaxed">
          {renderEmphasis(subtitle, "font-bold text-primary")}
        </p>
        <div className="mt-6 border border-border h-11 flex items-center px-3 text-sm text-muted-foreground">{searchPlaceholder || "—"}</div>
        <p className="mt-6 text-xs uppercase tracking-wider text-muted-foreground">Playlist heading</p>
        <p className="font-display text-xl font-bold tracking-tight">{playlistHeading || "—"}</p>
      </div>
    </div>
  );
}

export function BrandingManager() {
  const { data: branding } = useBranding();
  const [wordmark, setWordmark] = useState("");
  const [showWordmark, setShowWordmark] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (branding && !loaded) {
      setWordmark(branding.wordmark || "");
      setShowWordmark(branding.show_wordmark !== false);
      setLoaded(true);
    }
  }, [branding, loaded]);

  const saveText = useBrandingMutation(settingsService.updateBranding, {
    onSuccess: () => toast.success("Branding updated"),
    onError: (e) => toast.error(apiError(e)),
  });
  const upload = useBrandingMutation(settingsService.uploadLogo, {
    onSuccess: () => toast.success("Logo uploaded — it's now live in the header"),
    onError: (e) => toast.error(apiError(e)),
  });
  const clear = useBrandingMutation(settingsService.clearLogo, {
    onSuccess: () => toast.success("Logo removed — reverted to default mark"),
    onError: (e) => toast.error(apiError(e)),
  });

  const logoUrl = branding?.logo_url;

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="admin-branding-manager">
      <div className="border border-border bg-card p-6">
        <h3 className="font-display font-bold mb-1">Site branding</h3>
        <p className="text-xs text-muted-foreground mb-5">Manage the logo and wordmark shown in the app header. Changes go live instantly.</p>

        <label className="text-xs uppercase tracking-wider font-bold text-muted-foreground block mb-2">Header logo</label>
        <div className="flex items-center gap-4 mb-3">
          <div className="h-14 w-14 border border-border bg-background flex items-center justify-center overflow-hidden shrink-0">
            {logoUrl ? <img src={logoUrl} alt="logo" className="h-full w-full object-contain" data-testid="branding-current-logo" />
                     : <GraduationCap className="h-6 w-6 text-primary" />}
          </div>
          <div className="flex flex-col gap-2">
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
              className="hidden" data-testid="branding-logo-file"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) upload.mutate(f); e.target.value = ""; }} />
            <Button variant="outline" size="sm" className="rounded-none gap-2" disabled={upload.isPending}
              onClick={() => fileRef.current?.click()} data-testid="branding-upload-button">
              <Upload className="h-4 w-4" /> {upload.isPending ? "Uploading..." : "Upload logo"}
            </Button>
            {logoUrl && (
              <button onClick={() => clear.mutate()} disabled={clear.isPending}
                className="text-xs text-rose-600 font-semibold text-left hover:underline" data-testid="branding-remove-logo">
                Remove logo
              </button>
            )}
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground mb-6">PNG, JPG, WEBP, SVG or GIF · max 1 MB · transparent PNG recommended.</p>

        <label className="text-xs uppercase tracking-wider font-bold text-muted-foreground block mb-1">Wordmark (site name)</label>
        <input value={wordmark} onChange={(e) => setWordmark(e.target.value)} placeholder="Learn Me"
          className="w-full border border-border p-3 bg-background text-sm mb-3 h-11" data-testid="branding-wordmark-input" />
        <label className="flex items-center gap-2 text-sm mb-5 cursor-pointer" data-testid="branding-show-wordmark">
          <input type="checkbox" checked={showWordmark} onChange={(e) => setShowWordmark(e.target.checked)} />
          Show the wordmark text next to the logo
        </label>

        <Button onClick={() => saveText.mutate({ wordmark: wordmark.trim() || "Learn Me", show_wordmark: showWordmark })}
          disabled={saveText.isPending} className="rounded-none bg-primary hover:opacity-90" data-testid="branding-save-button">
          {saveText.isPending ? "Saving..." : "Save branding"}
        </Button>
      </div>

      <div className="border border-border bg-card p-6" data-testid="branding-preview">
        <p className="text-xs uppercase tracking-wider font-bold text-muted-foreground mb-4">Header preview</p>
        <div className="border border-border bg-background h-16 flex items-center px-6 gap-2">
          {logoUrl ? <img src={logoUrl} alt="logo" className="h-8 w-auto max-w-[180px] object-contain" />
                   : <div className="h-8 w-8 bg-primary text-primary-foreground flex items-center justify-center"><GraduationCap className="h-5 w-5" /></div>}
          {showWordmark && <span className="font-display font-extrabold text-lg tracking-tight">{wordmark || "Learn Me"}</span>}
        </div>
        <p className="mt-4 text-xs text-muted-foreground">This is exactly how your brand appears in the top-left of every page.</p>
      </div>

      <DojoTrackManager />
      <DojoGuideManager />
      <ExpertMeAiTextManager />
    </div>
  );
}


function DojoTrackManager() {
  const { data: dojo } = useDojoAdmin();
  const fileRef = useRef(null);

  const upload = useDojoMutation(settingsService.uploadDojoTrack, {
    onSuccess: () => toast.success("Dojo track uploaded — it's now the ambient audio"),
    onError: (e) => toast.error(apiError(e)),
  });
  const clear = useDojoMutation(settingsService.clearDojoTrack, {
    onSuccess: () => toast.success("Track removed — reverted to the synthesized ambient"),
    onError: (e) => toast.error(apiError(e)),
  });

  const hasTrack = dojo?.has_track;
  const sizeMb = dojo?.size ? (dojo.size / (1024 * 1024)).toFixed(1) : null;

  return (
    <div className="border border-border bg-card p-6 lg:col-span-2" data-testid="admin-dojo-manager">
      <div className="flex items-center gap-2 mb-1">
        <Music className="h-4 w-4 text-emerald-600" />
        <h3 className="font-display font-bold">Dojo Mode audio</h3>
      </div>
      <p className="text-xs text-muted-foreground mb-5">
        Upload a custom ambient track (looped, site-wide) to replace the synthesized water/flute/koto bed.
        It fades in/out with the Grasshopper toggle and ducks to 50% during a course. Remove it to fall back to the synth.
      </p>

      <div className="flex items-center gap-4">
        <div className={`h-14 w-14 border flex items-center justify-center shrink-0 ${hasTrack ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-600" : "border-border bg-background text-muted-foreground"}`}>
          <Music className="h-6 w-6" />
        </div>
        <div className="flex-1 min-w-0">
          {hasTrack ? (
            <div className="text-sm" data-testid="dojo-current-track">
              <span className="font-semibold truncate block">{dojo.track_name || "Custom track"}</span>
              <span className="text-xs text-muted-foreground">{[dojo.content_type, sizeMb && `${sizeMb} MB`].filter(Boolean).join(" · ")}</span>
            </div>
          ) : (
            <span className="text-sm text-muted-foreground" data-testid="dojo-no-track">No custom track — using the synthesized ambient.</span>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <input ref={fileRef} type="file" accept="audio/mpeg,audio/mp4,audio/aac,audio/ogg,audio/wav,audio/webm,.mp3,.m4a,.aac,.ogg,.oga,.wav,.webm"
            className="hidden" data-testid="dojo-track-file"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) upload.mutate(f); e.target.value = ""; }} />
          <Button variant="outline" size="sm" className="rounded-none gap-2" disabled={upload.isPending}
            onClick={() => fileRef.current?.click()} data-testid="dojo-upload-button">
            <Upload className="h-4 w-4" /> {upload.isPending ? "Uploading..." : hasTrack ? "Replace track" : "Upload track"}
          </Button>
          {hasTrack && (
            <button onClick={() => clear.mutate()} disabled={clear.isPending}
              className="text-xs text-rose-600 font-semibold text-left hover:underline" data-testid="dojo-remove-track">
              Remove track
            </button>
          )}
        </div>
      </div>
      <p className="text-[11px] text-muted-foreground mt-4">MP3, M4A, AAC, OGG, WAV or WEBM · max 20 MB · a seamless loop sounds best.</p>
    </div>
  );
}


function DojoGuideManager() {
  const { data: guide } = useGuideAdmin();
  const fileRef = useRef(null);
  const [name, setName] = useState("");

  useEffect(() => { if (guide && !name) setName(guide.name || ""); }, [guide]); // eslint-disable-line

  const saveName = useGuideMutation(settingsService.updateGuide, {
    onSuccess: () => toast.success("Sage name updated"),
    onError: (e) => toast.error(apiError(e)),
  });
  const upload = useGuideMutation(settingsService.uploadGuideImage, {
    onSuccess: () => toast.success("Sage portrait updated — synced across the app"),
    onError: (e) => toast.error(apiError(e)),
  });
  const clear = useGuideMutation(settingsService.clearGuideImage, {
    onSuccess: () => toast.success("Reverted to the default Sage portrait"),
    onError: (e) => toast.error(apiError(e)),
  });

  const raw = guide?.image_url || "";
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const preview = raw ? (raw.startsWith("/api") ? `${BACKEND}${raw}` : raw) : "/dojo-guide-default.png";
  const hasCustom = !!raw;

  return (
    <div className="border border-border bg-card p-6 lg:col-span-2" data-testid="admin-guide-manager">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="h-4 w-4 text-emerald-600" />
        <h3 className="font-display font-bold">Dojo Guide (Wise Mentor)</h3>
      </div>
      <p className="text-xs text-muted-foreground mb-5">
        The Sage who greets learners in the Dojo and leads the breathing session. The name and portrait
        here sync app-wide instantly.
      </p>

      <div className="flex items-center gap-5">
        <img src={preview} alt="Sage" data-testid="guide-preview"
          className="h-20 w-20 rounded-full object-cover ring-2 ring-emerald-500/40 shrink-0 bg-black" />
        <div className="flex-1 min-w-0 space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wider mb-1 block">Sage name</Label>
            <div className="flex gap-2">
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Donnyllama"
                className="h-9 text-sm rounded-none" data-testid="guide-name-input" />
              <Button variant="outline" size="sm" className="rounded-none shrink-0"
                disabled={saveName.isPending || !name.trim()}
                onClick={() => saveName.mutate({ name: name.trim() })} data-testid="guide-name-save">
                {saveName.isPending ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp"
              className="hidden" data-testid="guide-image-file"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) upload.mutate(f); e.target.value = ""; }} />
            <Button variant="outline" size="sm" className="rounded-none gap-2" disabled={upload.isPending}
              onClick={() => fileRef.current?.click()} data-testid="guide-upload-button">
              <Upload className="h-4 w-4" /> {upload.isPending ? "Uploading…" : hasCustom ? "Replace portrait" : "Upload portrait"}
            </Button>
            {hasCustom && (
              <button onClick={() => clear.mutate()} disabled={clear.isPending}
                className="text-xs text-rose-600 font-semibold hover:underline" data-testid="guide-clear-image">
                Use default
              </button>
            )}
          </div>
        </div>
      </div>
      <p className="text-[11px] text-muted-foreground mt-4">PNG, JPG or WEBP · max 5 MB · a square headshot on a dark background looks best.</p>
    </div>
  );
}


function ExpertMeAiTextManager() {
  const { data: xm } = useExpertMeAiAdmin();
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (xm && !form) {
      setForm({
        brand_name: xm.brand_name || "",
        tagline: xm.tagline || "",
        banner_eyebrow: xm.banner_eyebrow || "",
        banner_headline: xm.banner_headline || "",
        banner_subtitle: xm.banner_subtitle || "",
        banner_cta: xm.banner_cta || "",
      });
    }
  }, [xm, form]);

  const save = useUpdateExpertMeAi({
    onSuccess: () => toast.success("ExpertMeAi text updated — live on the site"),
    onError: (e) => toast.error(apiError(e)),
  });

  if (!form) return null;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const field = (k, label, { textarea } = {}) => (
    <div>
      <Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>
      {textarea ? (
        <textarea value={form[k]} onChange={set(k)} rows={2} data-testid={`xm-${k}`}
          className="mt-1.5 w-full border border-input bg-background px-3 py-2 text-sm rounded-none focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none" />
      ) : (
        <Input value={form[k]} onChange={set(k)} className="mt-1.5 rounded-none" data-testid={`xm-${k}`} />
      )}
    </div>
  );

  return (
    <div className="border border-border bg-card p-6 lg:col-span-2" data-testid="admin-expertmeai-manager">
      <div className="flex items-center gap-2 mb-1">
        <Landmark className="h-4 w-4 text-amber-600" />
        <h3 className="font-display font-bold">ExpertMeAi text</h3>
      </div>
      <p className="text-xs text-muted-foreground mb-5">
        Edit the ExpertMeAi brand name &amp; tagline (header + Experts page) and the homepage Featured Exhibit banner copy. Changes go live instantly.
      </p>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-4">
          {field("brand_name", "Brand name (header)")}
          {field("tagline", "Tagline (Experts page)")}
          {field("banner_eyebrow", "Banner eyebrow")}
          {field("banner_headline", "Banner headline")}
          {field("banner_subtitle", "Banner subtitle", { textarea: true })}
          {field("banner_cta", "Button label")}
          <Button onClick={() => save.mutate(form)} disabled={save.isPending}
            className="rounded-none gap-2 bg-amber-500 hover:bg-amber-400 text-[#170f04]" data-testid="xm-save-button">
            {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Save ExpertMeAi text
          </Button>
        </div>

        {/* Live preview */}
        <div className="space-y-4">
          <div>
            <p className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground mb-2">Header lockup</p>
            <div className="border border-border bg-background h-14 flex items-center px-4 gap-2" data-testid="xm-preview-header">
              <span className="h-8 w-8 bg-gradient-to-br from-amber-400 to-amber-600 text-[#1a1206] grid place-items-center"><Landmark className="h-4 w-4" /></span>
              <span className="font-display font-extrabold text-lg tracking-tight text-amber-600">{form.brand_name || "ExpertMeAi"}</span>
            </div>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground mb-2">Homepage banner</p>
            <div className="relative overflow-hidden border border-amber-500/30 min-h-[220px] flex flex-col justify-end" data-testid="xm-preview-banner">
              <div className="absolute inset-0">
                <img src="/expertmeai-scholar.jpg" alt="" className="h-full w-full object-cover brightness-[0.8] blur-[1px]" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0c0903]/95 via-[#170f04]/65 to-[#170f04]/15" />
              </div>
              <div className="relative p-5">
                <p className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold text-amber-300 mb-2"><Sparkles className="h-3 w-3" /> {form.banner_eyebrow || "Featured Exhibit"}</p>
                <h4 className="font-display text-lg font-extrabold text-amber-50 leading-tight">{form.banner_headline || "Want to learn from the best?"}</h4>
                <p className="mt-1.5 text-amber-100/85 text-xs leading-relaxed">{form.banner_subtitle || "Explore ExpertMeAi — authentic mentorship from the world's greatest minds."}</p>
                <span className="mt-3 inline-flex items-center gap-2 h-9 px-4 bg-amber-400 text-[#170f04] font-display font-bold text-sm">{form.banner_cta || "Explore Experts"} <ArrowRight className="h-3.5 w-3.5" /></span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


function RevenueChart({ series = [] }) {
  const max = Math.max(...series.map((d) => d.amount), 1);
  const total = series.reduce((s, d) => s + d.amount, 0);
  return (
    <div className="border border-border bg-card p-6 mb-6" data-testid="revenue-chart">
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-display font-bold">Revenue — last 30 days</h3>
        <span className="text-sm text-muted-foreground">Total ${total.toFixed(2)}</span>
      </div>
      <div className="flex items-end gap-[3px] h-32">
        {series.map((d) => (
          <div key={d.date} className="flex-1 group relative flex items-end h-full" title={`${d.date}: $${d.amount.toFixed(2)}`}>
            <div className="w-full bg-primary/80 hover:bg-primary transition-colors" style={{ height: `${Math.max((d.amount / max) * 100, d.amount > 0 ? 6 : 1)}%` }} />
          </div>
        ))}
      </div>
      <div className="flex justify-between mt-2 text-[10px] text-muted-foreground">
        <span>{series[0]?.date?.slice(5)}</span>
        <span>{series[series.length - 1]?.date?.slice(5)}</span>
      </div>
    </div>
  );
}

const ALERT_SEVERITY = {
  critical: { dot: "bg-rose-500", text: "text-rose-600" },
  warning: { dot: "bg-amber-400", text: "text-amber-600" },
  info: { dot: "bg-sky-400", text: "text-sky-600" },
};

export function AlertsLog() {
  const { data } = useAlerts();
  const alerts = data?.alerts || [];
  const resolve = useResolveAlert({
    onSuccess: () => toast.success("Alert resolved"),
    onError: (e) => toast.error(apiError(e)),
  });
  return (
    <div className="border border-border bg-card" data-testid="admin-alerts-log">
      <div className="flex items-center justify-between px-5 py-3 border-b border-border">
        <span className="font-display font-bold flex items-center gap-2"><ListChecks className="h-4 w-4 text-primary" /> Alerts Log</span>
        <span className="text-xs text-muted-foreground">{data?.active_count ?? 0} active</span>
      </div>
      {alerts.length === 0 ? (
        <p className="text-muted-foreground text-sm p-5" data-testid="alerts-log-empty">No alerts recorded. All systems clear.</p>
      ) : (
        <Table>
          <TableHeader><TableRow><TableHead>Severity</TableHead><TableHead>Type</TableHead><TableHead>Message</TableHead><TableHead>When</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
          <TableBody>
            {alerts.map((a) => {
              const sv = ALERT_SEVERITY[a.severity] || ALERT_SEVERITY.info;
              return (
                <TableRow key={a.id} data-testid={`alert-row-${a.id}`}>
                  <TableCell><span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${sv.text}`}><span className={`h-2 w-2 rounded-full ${sv.dot}`} /> {a.severity}</span></TableCell>
                  <TableCell className="capitalize text-sm">{a.type}</TableCell>
                  <TableCell className="text-sm">{a.message}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{a.at ? new Date(a.at).toLocaleString() : "—"}</TableCell>
                  <TableCell>
                    {a.resolved ? (
                      <span className="text-xs text-primary font-semibold flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> resolved</span>
                    ) : a.source === "stored" ? (
                      <Button size="sm" variant="outline" className="rounded-none h-8" onClick={() => resolve.mutate(a.id)} disabled={resolve.isPending} data-testid={`alert-resolve-${a.id}`}>Resolve</Button>
                    ) : (
                      <span className="text-xs text-muted-foreground">auto</span>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

const TXN_STATUS_STYLE = {
  paid: "text-emerald-600", refunded: "text-sky-600",
  initiated: "text-muted-foreground", expired: "text-amber-600", failed: "text-rose-600",
};

export function TransactionsPanel() {
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const { data } = useTransactions({ ...(status ? { status } : {}), ...(q ? { q } : {}) });
  const refund = useRefund({
    onSuccess: (d) => toast.success(`Refunded (${d.reversed})`),
    onError: (e) => toast.error(apiError(e)),
  });
  const txns = data?.transactions || [];
  const s = data?.summary || { count: 0, gross_paid: 0, refunded_count: 0 };
  const FILTERS = ["", "paid", "refunded", "expired", "initiated"];
  return (
    <div className="space-y-4" data-testid="admin-transactions-panel">
      <div className="grid grid-cols-3 gap-3">
        <div className="border border-border p-4"><div className="font-display text-2xl font-bold">{s.count}</div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Transactions</div></div>
        <div className="border border-border p-4"><div className="font-display text-2xl font-bold text-emerald-600">${s.gross_paid}</div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Gross paid</div></div>
        <div className="border border-border p-4"><div className="font-display text-2xl font-bold text-sky-600">{s.refunded_count}</div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Refunded</div></div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {FILTERS.map((f) => (
          <button key={f || "all"} onClick={() => setStatus(f)} data-testid={`txn-filter-${f || "all"}`}
            className={`px-3 h-9 text-xs font-semibold border capitalize ${status === f ? "border-primary bg-primary text-white" : "border-border hover:border-foreground"}`}>
            {f || "all"}
          </button>
        ))}
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email or package…" className="rounded-none h-9 max-w-xs ml-auto" data-testid="txn-search" />
      </div>
      <div className="border border-border bg-card overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Email</TableHead><TableHead>Package</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {txns.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-sm text-muted-foreground py-6 text-center">No transactions.</TableCell></TableRow>
            ) : txns.map((t) => (
              <TableRow key={t.session_id} data-testid={`txn-row-${t.session_id}`}>
                <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{t.created_at ? new Date(t.created_at).toLocaleDateString() : "—"}</TableCell>
                <TableCell className="text-sm">{t.email}</TableCell>
                <TableCell className="text-sm">{t.package_id}{t.coins ? ` · ${t.coins} coins` : ""}</TableCell>
                <TableCell className="font-medium">${t.amount.toFixed(2)}</TableCell>
                <TableCell><span className={`text-xs font-semibold capitalize ${TXN_STATUS_STYLE[t.display_status] || ""}`}>{t.display_status}</span></TableCell>
                <TableCell>
                  {t.display_status === "paid" && (
                    <Button size="sm" variant="outline" onClick={() => { if (window.confirm(`Refund $${t.amount.toFixed(2)} to ${t.email}?`)) refund.mutate(t.session_id); }}
                      disabled={refund.isPending} className="rounded-none h-8 gap-1" data-testid={`txn-refund-${t.session_id}`}>
                      <RotateCcw className="h-3.5 w-3.5" /> Refund
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

const MOD_SRC_LABEL = { course: "Member", guest: "Guest", admin_featured: "Admin" };

export function ModerationLog() {
  const { data: events = [] } = useModerationLog();
  return (
    <div className="border border-border bg-card" data-testid="admin-moderation-log">
      <div className="px-5 py-3 border-b border-border">
        <div className="flex items-center gap-2">
          <ShieldAlert className="h-4 w-4 text-primary" />
          <span className="font-display font-bold">Safety Logs — flagged &amp; rejected prompts ({events.length})</span>
        </div>
        <p className="text-xs text-muted-foreground mt-1">Prompts our family-safe filter rejected. Monitor here for repeat offenders and bad actors.</p>
      </div>
      {events.length === 0 ? (
        <p className="text-sm text-muted-foreground p-5" data-testid="moderation-log-empty">No prompts have been flagged or rejected yet — the Family Safe filter is clear.</p>
      ) : (
        <Table>
          <TableHeader><TableRow><TableHead>Topic</TableHead><TableHead>Category</TableHead><TableHead>Source</TableHead><TableHead>Requester</TableHead><TableHead>When</TableHead></TableRow></TableHeader>
          <TableBody>
            {events.map((e) => (
              <TableRow key={e.id} data-testid={`moderation-row-${e.id}`}>
                <TableCell className="text-sm font-medium max-w-xs truncate">{e.topic}</TableCell>
                <TableCell><span className="text-xs font-semibold text-rose-600 capitalize">{e.category || "—"}</span></TableCell>
                <TableCell className="text-sm">{MOD_SRC_LABEL[e.source] || e.source}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{e.requester || "—"}</TableCell>
                <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{e.at ? new Date(e.at).toLocaleString() : "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}


const FEEDBACK_TYPE_META = {
  bug: { icon: Bug, cls: "text-rose-600", chip: "bg-rose-500/10 text-rose-600", label: "bug" },
  comment: { icon: MessageSquare, cls: "text-sky-600", chip: "bg-sky-500/10 text-sky-600", label: "comment" },
  inspiration: { icon: Lightbulb, cls: "text-amber-600", chip: "bg-amber-500/10 text-amber-600", label: "inspiration" },
  agent_suggestion: { icon: Sparkles, cls: "text-violet-600", chip: "bg-violet-500/10 text-violet-600", label: "agent" },
};

function ScreenshotThumb({ path }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let active = true; let obj;
    adminService.feedbackScreenshot(path)
      .then((blob) => { if (active) { obj = URL.createObjectURL(blob); setUrl(obj); } })
      .catch(() => {});
    return () => { active = false; if (obj) URL.revokeObjectURL(obj); };
  }, [path]);
  if (!url) return <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground mt-1"><ImageIcon className="h-3.5 w-3.5" /> loading…</span>;
  return (
    <a href={url} target="_blank" rel="noreferrer" className="inline-block mt-2" data-testid={`feedback-screenshot-${path}`}>
      <img src={url} alt="screenshot" className="h-14 w-auto rounded border border-border hover:opacity-80 transition-opacity" />
    </a>
  );
}

const TRIAGE_OPTS = {
  severity: ["Low", "Med", "High", "Critical"],
  priority: ["P1", "P2", "P3", "P4"],
  assignee: ["Neo", "Emmy", "Admin", "Tester"],
  status: ["New", "Triaging", "Fix Pending", "UAT Ready", "Resolved"],
};
const STATUS_CLS = {
  New: "border-rose-500 text-rose-600 bg-rose-500/10",
  Triaging: "border-amber-500 text-amber-600 bg-amber-500/10",
  "Fix Pending": "border-sky-500 text-sky-600 bg-sky-500/10",
  "UAT Ready": "border-fuchsia-500 text-fuchsia-600 bg-fuchsia-500/10",
  Resolved: "border-emerald-500 text-emerald-600 bg-emerald-500/10",
  "Future Backlog": "border-violet-500 text-violet-600 bg-violet-500/10",
  Deployed: "border-teal-500 text-teal-600 bg-teal-500/10",
  Rejected: "border-rose-700 text-rose-700 bg-rose-700/10",
};
// The Feedback Lab status dropdown adds "Rejected" (which triggers the archive flow).
const LAB_STATUS_OPTS = ["New", "Triaging", "Fix Pending", "UAT Ready", "Resolved", "Rejected"];
const SEVERITY_CLS = {
  Low: "text-muted-foreground", Med: "text-amber-600", High: "text-orange-600", Critical: "text-rose-600",
};

// ECU = Estimated Cost Units badge (Emmy-estimated, admin-overridable).
function EcuBadge({ value, className = "" }) {
  return (
    <span title="Estimated Cost Units — Emmy's estimate, editable in triage"
      className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 border border-amber-500 text-amber-700 bg-amber-500/10 ${className}`}
      data-testid="ecu-badge">
      <Zap className="h-3 w-3" /> {value ?? 0} ECU
    </span>
  );
}

function TriageSelect({ field, value, onChange, testid, className = "", options }) {
  const opts = options || TRIAGE_OPTS[field];
  return (
    <select value={value} onChange={(e) => onChange(field, e.target.value)} data-testid={testid}
      className={`h-8 border text-xs font-semibold px-1.5 bg-background outline-none ${className}`}>
      {opts.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function FeedbackLab() {
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [replyOpen, setReplyOpen] = useState(null);
  const [replyText, setReplyText] = useState({});
  const [showArchived, setShowArchived] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const params = {};
  if (q.trim()) params.q = q.trim();
  if (typeFilter !== "all") params.type = typeFilter;
  if (showArchived) params.include_archived = true;
  const { data, isLoading } = useAdminFeedback(params);
  const resolve = useResolveFeedback({ onError: (e) => toast.error(apiError(e)) });
  const updateTriage = useUpdateFeedback({ onError: (e) => toast.error(apiError(e)) });
  const reject = useRejectFeedback({
    onSuccess: () => { toast.success("Idea rejected & archived"); setRejectTarget(null); setRejectReason(""); },
    onError: (e) => toast.error(apiError(e)),
  });
  const restore = useRestoreFeedback({
    onSuccess: () => toast.success("Restored to the board"),
    onError: (e) => toast.error(apiError(e)),
  });
  const reply = useReplyFeedback({
    onSuccess: (_d, vars) => { toast.success("Reply sent to tester"); setReplyText((r) => ({ ...r, [vars.id]: "" })); },
    onError: (e) => toast.error(apiError(e)),
  });
  const rows = data?.feedback || [];
  const summary = data?.summary || { total: 0, bugs: 0, comments: 0, inspirations: 0, agent_suggestions: 0, open: 0, rejected: 0 };

  const setField = (f) => (field, value) => {
    if (field === "status") {
      if (f.archived) {
        if (value === "Rejected") return;            // already archived — no-op
        restore.mutate({ id: f.id, status: value }); // any active status restores it
        return;
      }
      if (value === "Rejected") { setRejectTarget(f); setRejectReason(""); return; }
    }
    updateTriage.mutate({ id: f.id, [field]: value });
  };
  const sendReply = (id) => {
    const msg = (replyText[id] || "").trim();
    if (!msg) return;
    reply.mutate({ id, message: msg });
  };

  return (
    <div className="space-y-4" data-testid="admin-feedback">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <Stat icon={MessageSquare} label="Total" value={summary.total} />
        <Stat icon={Bug} label="Bugs" value={summary.bugs} />
        <Stat icon={MessageSquare} label="Comments" value={summary.comments} />
        <Stat icon={Lightbulb} label="Inspirations" value={summary.inspirations} />
        <Stat icon={Sparkles} label="Agent Sugg." value={summary.agent_suggestions} />
        <Stat icon={FlaskConical} label="Open" value={summary.open} />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex-1 min-w-[200px]">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search message, email or URL…"
            className="rounded-none h-10" data-testid="feedback-search" />
        </div>
        <div className="flex flex-wrap gap-1" data-testid="feedback-type-filter">
          {[{ k: "all", l: "All" }, { k: "bug", l: "Bugs" }, { k: "comment", l: "Comments" }, { k: "inspiration", l: "Inspirations" }, { k: "agent_suggestion", l: "Agent" }].map((t) => (
            <button key={t.k} onClick={() => setTypeFilter(t.k)} data-testid={`feedback-filter-${t.k}`}
              className={`px-3 h-10 text-xs font-semibold border ${typeFilter === t.k ? "border-primary bg-primary text-white" : "border-border hover:border-foreground"}`}>
              {t.l}
            </button>
          ))}
          <button onClick={() => setShowArchived((v) => !v)} data-testid="feedback-show-archived"
            className={`px-3 h-10 text-xs font-semibold border inline-flex items-center gap-1.5 ${showArchived ? "border-rose-600 bg-rose-600 text-white" : "border-border hover:border-foreground text-rose-600"}`}>
            <Ban className="h-3.5 w-3.5" /> {showArchived ? "Hide Archived" : "Show Archived"}{summary.rejected ? ` (${summary.rejected})` : ""}
          </button>
        </div>
      </div>

      <div className="border border-border bg-card overflow-x-auto">
        {isLoading ? (
          <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : rows.length === 0 ? (
          <p className="text-muted-foreground text-sm p-5">No feedback yet.</p>
        ) : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Type</TableHead><TableHead>Report & Emmy analysis</TableHead><TableHead>Triage</TableHead>
              <TableHead>From</TableHead><TableHead>When</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((f) => {
                const meta = FEEDBACK_TYPE_META[f.type] || FEEDBACK_TYPE_META.bug;
                const Icon = meta.icon;
                const replies = f.replies || [];
                const onField = setField(f);
                return (
                  <Fragment key={f.id}>
                  <TableRow className={f.archived ? "opacity-70 bg-rose-500/5" : f.resolved ? "opacity-60" : ""} data-testid={`feedback-row-${f.id}`}>
                    <TableCell>
                      <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold px-2 py-1 ${meta.chip}`}>
                        <Icon className="h-3.5 w-3.5" /> {f.type}
                      </span>
                      {f.archived && (
                        <span className="mt-1.5 flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-1 border border-rose-600 text-rose-700 bg-rose-600/10 w-fit" data-testid={`feedback-rejected-badge-${f.id}`}>
                          <Ban className="h-3 w-3" /> Rejected
                        </span>
                      )}
                      <div className="mt-1.5"><EcuBadge value={f.ecu} /></div>
                    </TableCell>
                    <TableCell className="max-w-sm">
                      <p className="text-sm whitespace-pre-wrap break-words">{f.message}</p>
                      {f.emmy_analysis ? (
                        <div className="mt-2 text-xs bg-indigo-500/5 border-l-2 border-indigo-500 pl-2.5 py-1.5 rounded-r" data-testid={`emmy-analysis-${f.id}`}>
                          <span className="inline-flex items-center gap-1 font-bold text-indigo-600"><Sparkles className="h-3 w-3" /> Emmy</span>
                          <span className="text-muted-foreground whitespace-pre-wrap"> {f.emmy_analysis}</span>
                        </div>
                      ) : (
                        <div className="mt-2 text-[11px] text-amber-600 inline-flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Emmy is analyzing…</div>
                      )}
                      {f.archived && f.rejection_reason ? (
                        <div className="mt-2 text-xs bg-rose-500/5 border-l-2 border-rose-600 pl-2.5 py-1.5 rounded-r" data-testid={`rejection-reason-${f.id}`}>
                          <span className="inline-flex items-center gap-1 font-bold text-rose-700"><Ban className="h-3 w-3" /> Rejected</span>
                          <span className="text-muted-foreground whitespace-pre-wrap"> {f.rejection_reason}</span>
                        </div>
                      ) : null}
                      {f.screenshot_path ? <ScreenshotThumb path={f.screenshot_path} /> : null}
                      {replies.length > 0 && <div className="text-[11px] text-emerald-600 mt-1">{replies.length} repl{replies.length > 1 ? "ies" : "y"} sent</div>}
                    </TableCell>
                    <TableCell>
                      <div className="grid grid-cols-2 gap-1.5 min-w-[210px]">
                        <TriageSelect field="status" value={f.status} onChange={onField} options={LAB_STATUS_OPTS} testid={`feedback-status-${f.id}`} className={`col-span-2 ${STATUS_CLS[f.status] || "border-border"}`} />
                        <TriageSelect field="severity" value={f.severity} onChange={onField} testid={`feedback-severity-${f.id}`} className={`border-border ${SEVERITY_CLS[f.severity] || ""}`} />
                        <TriageSelect field="priority" value={f.priority} onChange={onField} testid={`feedback-priority-${f.id}`} className="border-border" />
                        <TriageSelect field="assignee" value={f.assignee} onChange={onField} testid={`feedback-assignee-${f.id}`} className="col-span-2 border-border" />
                        <label className="col-span-2 flex items-center gap-1.5 text-[11px] font-semibold text-amber-700">
                          <Zap className="h-3 w-3" /> ECU
                          <input type="number" min="0" max="999" key={f.ecu ?? 0} defaultValue={f.ecu ?? 0}
                            onBlur={(e) => { const v = parseInt(e.target.value, 10); if (!Number.isNaN(v) && v !== (f.ecu ?? 0)) onField("ecu", v); }}
                            data-testid={`feedback-ecu-${f.id}`}
                            className="w-16 h-7 border border-amber-500/60 bg-background px-1.5 text-xs text-foreground outline-none focus:border-amber-500" />
                        </label>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs">
                      <div className="font-medium">{f.user_email}</div>
                      <div className="text-muted-foreground">{f.user_role} · {f.plan_type}</div>
                      <div className="text-muted-foreground truncate max-w-[140px]" title={`${f.url}\n${f.user_agent} · ${f.viewport}`}>
                        {(() => { try { return new URL(f.url).pathname; } catch { return f.url || "—"; } })()}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{f.created_at ? new Date(f.created_at).toLocaleString() : "—"}</TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1.5">
                        {f.archived ? (
                          <button onClick={() => restore.mutate({ id: f.id, status: "Triaging" })}
                            disabled={restore.isPending} data-testid={`feedback-restore-${f.id}`}
                            className="inline-flex items-center gap-1.5 px-3 h-8 text-xs font-semibold border border-emerald-500 text-emerald-600 whitespace-nowrap hover:bg-emerald-500 hover:text-white transition-colors">
                            <RotateCcw className="h-3.5 w-3.5" /> Restore
                          </button>
                        ) : (
                        <button onClick={() => setReplyOpen(replyOpen === f.id ? null : f.id)} data-testid={`feedback-reply-toggle-${f.id}`}
                          className="inline-flex items-center gap-1.5 px-3 h-8 text-xs font-semibold border border-indigo-500 text-indigo-600 whitespace-nowrap">
                          <MessageSquare className="h-3.5 w-3.5" /> Reply
                        </button>
                        )}
                        {!f.archived && f.status === "New" && (
                          <button onClick={() => updateTriage.mutate({ id: f.id, status: "Fix Pending", assignee: "Neo" })}
                            disabled={updateTriage.isPending} data-testid={`feedback-convert-task-${f.id}`}
                            className="inline-flex items-center gap-1.5 px-3 h-8 text-xs font-semibold border border-violet-500 text-violet-600 whitespace-nowrap hover:bg-violet-500 hover:text-white transition-colors">
                            <ListChecks className="h-3.5 w-3.5" /> Convert to Task
                          </button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                  {replyOpen === f.id && (
                    <TableRow data-testid={`feedback-reply-row-${f.id}`}>
                      <TableCell colSpan={6} className="bg-muted/40">
                        {replies.length > 0 && (
                          <div className="space-y-1 mb-3">
                            {replies.map((r, i) => (
                              <div key={i} className="text-sm border-l-2 border-indigo-500 pl-3">
                                <span className="text-xs font-semibold text-indigo-600">{r.by}:</span> {r.message}
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="flex gap-2">
                          <Input value={replyText[f.id] || ""} onChange={(e) => setReplyText((r) => ({ ...r, [f.id]: e.target.value }))}
                            onKeyDown={(e) => e.key === "Enter" && sendReply(f.id)}
                            placeholder="Reply to the tester — sent to their notification bell…"
                            className="rounded-none h-10" data-testid={`feedback-reply-input-${f.id}`} />
                          <Button onClick={() => sendReply(f.id)} disabled={reply.isPending} className="rounded-none bg-primary hover:opacity-90" data-testid={`feedback-reply-send-${f.id}`}>
                            <Send className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={!!rejectTarget} onOpenChange={(o) => { if (!o) { setRejectTarget(null); setRejectReason(""); } }}>
        <DialogContent className="rounded-none max-w-md" data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700"><Ban className="h-4 w-4" /> Reject this idea</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground -mt-1">
            This archives the item off the board. Record why it was killed — the note is kept with the archived record.
          </p>
          {rejectTarget && (
            <p className="text-xs bg-muted/50 border-l-2 border-rose-500 pl-2.5 py-1.5 whitespace-pre-wrap break-words max-h-24 overflow-y-auto">
              {rejectTarget.message}
            </p>
          )}
          <textarea
            value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Rejection reason (e.g. out of scope, duplicate, not aligned with roadmap)…"
            data-testid="reject-reason-input" rows={4} autoFocus
            className="w-full border border-border rounded-none p-2.5 text-sm outline-none focus:border-rose-500 resize-none" />
          <div className="flex justify-end gap-2">
            <Button variant="outline" className="rounded-none" onClick={() => { setRejectTarget(null); setRejectReason(""); }} data-testid="reject-cancel">
              Cancel
            </Button>
            <Button className="rounded-none bg-rose-600 hover:bg-rose-700 text-white gap-2"
              disabled={reject.isPending}
              onClick={() => reject.mutate({ id: rejectTarget.id, reason: rejectReason.trim() })} data-testid="reject-confirm">
              <Ban className="h-4 w-4" /> {reject.isPending ? "Rejecting…" : "Reject & Archive"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const PROMO_THEME_OPTS = ["indigo", "violet", "primary", "amber", "emerald", "rose"];
const PROMO_ICON_OPTS = ["compass", "sparkles", "crown", "coins", "rocket", "zap", "star", "gift", "bell", "flask"];

export function PromosManager() {
  const { data: initial } = useAdminPromos();
  const [list, setList] = useState(null);
  useEffect(() => { if (initial && list === null) setList(initial.map((p) => ({ ...p }))); }, [initial, list]);
  const save = useUpdatePromos({
    onSuccess: () => toast.success("Promos updated — live on the site"),
    onError: (e) => toast.error(apiError(e)),
  });
  const rows = list || [];
  const set = (i, k, v) => setList(rows.map((p, idx) => (idx === i ? { ...p, [k]: v } : p)));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= rows.length) return;
    const copy = [...rows];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    setList(copy);
  };
  const remove = (i) => setList(rows.filter((_, idx) => idx !== i));
  const add = () => setList([...rows, { id: "", enabled: true, theme: "primary", icon: "sparkles", badge: "", title: "New promo", tagline: "", body: "", link: "", cta: "" }]);

  return (
    <div className="space-y-4" data-testid="admin-promos">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-muted-foreground">Manage the "From Learn Me" promo rail cards. Changes go live instantly.</p>
        <div className="flex gap-2">
          <Button onClick={add} variant="outline" className="rounded-none gap-2" data-testid="promo-add"><Plus className="h-4 w-4" /> Add card</Button>
          <Button onClick={() => save.mutate(rows)} disabled={save.isPending} className="rounded-none bg-primary hover:opacity-90 gap-2" data-testid="promos-save">
            {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save changes
          </Button>
        </div>
      </div>

      <div className="space-y-4">
        {rows.map((p, i) => (
          <div key={i} className={`border p-4 ${p.enabled ? "border-border bg-card" : "border-dashed border-border bg-muted/30 opacity-70"}`} data-testid={`promo-editor-${i}`}>
            <div className="flex items-center justify-between mb-3 gap-2">
              <div className="flex items-center gap-2">
                <Switch checked={p.enabled} onCheckedChange={(v) => set(i, "enabled", v)} data-testid={`promo-enabled-${i}`} />
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{p.enabled ? "Live" : "Hidden"}</span>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => move(i, -1)} className="h-8 w-8 border border-border flex items-center justify-center hover:border-foreground" data-testid={`promo-up-${i}`}><ChevronUp className="h-4 w-4" /></button>
                <button onClick={() => move(i, 1)} className="h-8 w-8 border border-border flex items-center justify-center hover:border-foreground" data-testid={`promo-down-${i}`}><ChevronDown className="h-4 w-4" /></button>
                <button onClick={() => remove(i)} className="h-8 w-8 border border-destructive text-destructive flex items-center justify-center hover:bg-destructive hover:text-white transition-colors" data-testid={`promo-delete-${i}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label className="text-xs">Title</Label><Input value={p.title} onChange={(e) => set(i, "title", e.target.value)} className="rounded-none h-9" data-testid={`promo-title-${i}`} /></div>
              <div><Label className="text-xs">Tagline</Label><Input value={p.tagline} onChange={(e) => set(i, "tagline", e.target.value)} className="rounded-none h-9" /></div>
              <div className="sm:col-span-2"><Label className="text-xs">Body</Label><Input value={p.body} onChange={(e) => set(i, "body", e.target.value)} className="rounded-none h-9" /></div>
              <div><Label className="text-xs">Badge</Label><Input value={p.badge} onChange={(e) => set(i, "badge", e.target.value)} className="rounded-none h-9" placeholder="e.g. Coming Soon" /></div>
              <div><Label className="text-xs">Link (optional)</Label><Input value={p.link} onChange={(e) => set(i, "link", e.target.value)} className="rounded-none h-9" placeholder="/pricing" /></div>
              <div><Label className="text-xs">CTA text (optional)</Label><Input value={p.cta} onChange={(e) => set(i, "cta", e.target.value)} className="rounded-none h-9" placeholder="Become a Scholar" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Theme</Label>
                  <select value={p.theme} onChange={(e) => set(i, "theme", e.target.value)} className="w-full h-9 border border-input bg-background text-sm px-2" data-testid={`promo-theme-${i}`}>
                    {PROMO_THEME_OPTS.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div><Label className="text-xs">Icon</Label>
                  <select value={p.icon} onChange={(e) => set(i, "icon", e.target.value)} className="w-full h-9 border border-input bg-background text-sm px-2" data-testid={`promo-icon-${i}`}>
                    {PROMO_ICON_OPTS.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
            </div>
          </div>
        ))}
        {rows.length === 0 && <p className="text-muted-foreground text-sm">No promo cards. Add one to get started.</p>}
      </div>
    </div>
  );
}

const BOARD_COLUMNS = ["New", "Triaging", "Fix Pending", "UAT Ready", "Resolved"];
const WIP_LIMITS = { Triaging: 6, "Fix Pending": 4 };
const COL_HEAD_CLS = {
  New: "text-rose-600 border-rose-500",
  Triaging: "text-amber-600 border-amber-500",
  "Fix Pending": "text-sky-600 border-sky-500",
  "UAT Ready": "text-fuchsia-600 border-fuchsia-500",
  Resolved: "text-emerald-600 border-emerald-500",
};
const sumEcu = (items) => items.reduce((t, i) => t + (i.ecu || 0), 0);

function FeedbackDetailModal({ item, onClose }) {
  const [replyMsg, setReplyMsg] = useState("");
  const update = useUpdateFeedback({ onError: (e) => toast.error(apiError(e)) });
  const reply = useReplyFeedback({
    onSuccess: () => { toast.success("Reply sent to tester"); setReplyMsg(""); },
    onError: (e) => toast.error(apiError(e)),
  });
  const [impFiles, setImpFiles] = useState("");
  const [funcChanges, setFuncChanges] = useState("");
  const [drafting, setDrafting] = useState(false);
  useEffect(() => { setImpFiles(item?.impacted_files || ""); setFuncChanges(item?.functional_changes || ""); }, [item?.id]);
  const emmyDraft = async () => {
    setDrafting(true);
    try {
      const r = await adminService.emmyDraft(item.id);
      setImpFiles(r.impacted_files || ""); setFuncChanges(r.functional_changes || "");
      toast.success("Emmy drafted the impact summary");
    } catch (e) { toast.error(apiError(e)); } finally { setDrafting(false); }
  };
  if (!item) return null;
  const meta = FEEDBACK_TYPE_META[item.type] || FEEDBACK_TYPE_META.bug;
  const Icon = meta.icon;
  const onField = (field, value) => update.mutate({ id: item.id, [field]: value });
  const ship = () => update.mutate({ id: item.id, status: "Fix Pending", assignee: "Neo" });
  const sendToUat = () => update.mutate({ id: item.id, shipped: true, impacted_files: impFiles, functional_changes: funcChanges });

  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="feedback-detail-modal">
      <div className="w-full max-w-lg bg-card border border-border rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border sticky top-0 bg-card">
          <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold px-2 py-1 ${meta.chip}`}>
            <Icon className="h-3.5 w-3.5" /> {meta.label}
          </span>
          <div className="flex items-center gap-2">
            <EcuBadge value={item.ecu} />
            <button onClick={onClose} data-testid="feedback-detail-close" className="text-muted-foreground hover:text-foreground"><XCircle className="h-5 w-5" /></button>
          </div>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm whitespace-pre-wrap break-words">{item.message}</p>
          {item.emmy_analysis ? (
            <div className="text-xs bg-indigo-500/5 border-l-2 border-indigo-500 pl-2.5 py-1.5 rounded-r">
              <span className="inline-flex items-center gap-1 font-bold text-indigo-600"><Sparkles className="h-3 w-3" /> Emmy</span>
              <span className="text-muted-foreground whitespace-pre-wrap"> {item.emmy_analysis}</span>
            </div>
          ) : null}
          {item.screenshot_path ? <ScreenshotThumb path={item.screenshot_path} /> : null}

          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Status</Label><TriageSelect field="status" value={item.status} onChange={onField} testid={`detail-status-${item.id}`} className={`w-full ${STATUS_CLS[item.status] || "border-border"}`} /></div>
            <div><Label className="text-xs">Assignee</Label><TriageSelect field="assignee" value={item.assignee} onChange={onField} testid={`detail-assignee-${item.id}`} className="w-full border-border" /></div>
            <div><Label className="text-xs">Severity</Label><TriageSelect field="severity" value={item.severity} onChange={onField} testid={`detail-severity-${item.id}`} className={`w-full border-border ${SEVERITY_CLS[item.severity] || ""}`} /></div>
            <div><Label className="text-xs">Priority</Label><TriageSelect field="priority" value={item.priority} onChange={onField} testid={`detail-priority-${item.id}`} className="w-full border-border" /></div>
            <div className="col-span-2">
              <Label className="text-xs flex items-center gap-1 text-amber-700"><Zap className="h-3 w-3" /> ECU (Estimated Cost Units)</Label>
              <input type="number" min="0" max="999" key={item.ecu ?? 0} defaultValue={item.ecu ?? 0}
                onBlur={(e) => { const v = parseInt(e.target.value, 10); if (!Number.isNaN(v) && v !== (item.ecu ?? 0)) onField("ecu", v); }}
                data-testid={`detail-ecu-${item.id}`}
                className="w-full h-9 border border-amber-500/60 bg-background px-2 text-sm outline-none focus:border-amber-500" />
            </div>
          </div>

          {item.status === "Fix Pending" && (
            <div className="border border-fuchsia-500/40 bg-fuchsia-500/5 rounded-xl p-3 space-y-2" data-testid={`uat-handoff-${item.id}`}>
              <div className="flex items-center justify-between">
                <div className="text-xs font-bold text-fuchsia-600 flex items-center gap-1"><Rocket className="h-3.5 w-3.5" /> Fix complete? Hand off to UAT</div>
                <button onClick={emmyDraft} disabled={drafting} data-testid={`emmy-draft-${item.id}`}
                  className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 border border-fuchsia-500 text-fuchsia-600 hover:bg-fuchsia-500/10 rounded">
                  {drafting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />} Emmy: auto-draft
                </button>
              </div>
              <div><Label className="text-xs">Impacted Files</Label>
                <textarea value={impFiles} onChange={(e) => setImpFiles(e.target.value)} rows={2} data-testid={`uat-impacted-${item.id}`}
                  className="w-full border border-border bg-background p-2 text-xs rounded outline-none focus:border-fuchsia-500" placeholder="e.g. Admin.jsx, dojo_core/routes/admin.py" /></div>
              <div><Label className="text-xs">Functional Changes</Label>
                <textarea value={funcChanges} onChange={(e) => setFuncChanges(e.target.value)} rows={3} data-testid={`uat-changes-${item.id}`}
                  className="w-full border border-border bg-background p-2 text-xs rounded outline-none focus:border-fuchsia-500" placeholder="What changed and how Emmy should verify it" /></div>
              <button onClick={sendToUat} disabled={update.isPending} data-testid={`send-to-uat-${item.id}`}
                className="w-full inline-flex items-center justify-center gap-2 h-9 rounded-xl font-bold text-white bg-fuchsia-600 hover:bg-fuchsia-700">
                <Rocket className="h-4 w-4" /> Mark fix complete → UAT Ready
              </button>
            </div>
          )}

          {item.type !== "bug" && item.status === "Future Backlog" && (
            <button onClick={ship} disabled={update.isPending} data-testid={`detail-ship-${item.id}`}
              className="w-full inline-flex items-center justify-center gap-2 h-10 rounded-xl font-display font-bold tracking-tight text-white" style={{ background: "linear-gradient(135deg, #7c3aed, #4f46e5)" }}>
              <Rocket className="h-4 w-4" /> Ship it — queue for Neo to build
            </button>
          )}

          <div className="text-[11px] text-muted-foreground space-y-0.5">
            <div>From: {item.user_email} · {item.user_role} / {item.plan_type}</div>
            {item.url ? <div className="truncate">URL: {item.url}</div> : null}
            <div>{item.viewport} · {item.created_at ? new Date(item.created_at).toLocaleString() : ""}</div>
          </div>

          {(item.replies || []).length > 0 && (
            <div className="space-y-1">
              {item.replies.map((r, i) => (
                <div key={i} className="text-xs border-l-2 border-indigo-500 pl-2.5"><span className="font-semibold text-indigo-600">{r.by}:</span> {r.message}</div>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <Input value={replyMsg} onChange={(e) => setReplyMsg(e.target.value)} placeholder="Reply to tester…" className="rounded-none h-10" data-testid={`detail-reply-input-${item.id}`}
              onKeyDown={(e) => { if (e.key === "Enter" && replyMsg.trim()) reply.mutate({ id: item.id, message: replyMsg.trim() }); }} />
            <Button onClick={() => replyMsg.trim() && reply.mutate({ id: item.id, message: replyMsg.trim() })} disabled={reply.isPending} className="rounded-none bg-primary hover:opacity-90" data-testid={`detail-reply-send-${item.id}`}>
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function KanbanBoard() {
  const { data } = useAdminFeedback({});
  const { data: digest } = useFeedbackDigest();
  const { data: wipLimits } = useWipLimits();
  const saveWip = useUpdateWipLimits({ onSuccess: () => toast.success("WIP limits saved"), onError: (e) => toast.error(apiError(e)) });
  const update = useUpdateFeedback({ onError: (e) => toast.error(apiError(e)) });
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [typeF, setTypeF] = useState("all");
  const [assigneeF, setAssigneeF] = useState("all");
  const [wipDraft, setWipDraft] = useState(null);
  const LIMITS = wipLimits || WIP_LIMITS;

  const allRows = data?.feedback || [];
  const rows = allRows.filter((r) =>
    (typeF === "all" || r.type === typeF) && (assigneeF === "all" || r.assignee === assigneeF));
  const byStatus = (s) => rows.filter((r) => (r.status || "New") === s);
  const shipped = rows.filter((r) => r.shipped && r.status !== "Resolved");

  const onDrop = (status, e) => {
    const id = dragId || (e && e.dataTransfer ? e.dataTransfer.getData("text/plain") : null);
    const item = allRows.find((r) => r.id === id);
    if (item && item.status !== status) update.mutate({ id, status });
    setDragId(null); setOverCol(null);
  };
  const shipIt = (id) => update.mutate({ id, status: "Fix Pending", assignee: "Neo", shipped: true });
  const wipVal = (col) => (wipDraft ? wipDraft[col] : LIMITS[col]) ?? "";
  const saveWipLimits = () => saveWip.mutate({ ...LIMITS, ...(wipDraft || {}) });

  return (
    <div data-testid="kanban-board">
      {digest && (
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mb-4 p-3 border border-border bg-muted/30 text-xs" data-testid="digest-strip">
          <span className="font-bold uppercase tracking-wider text-muted-foreground">This week</span>
          <span><b className="text-foreground">{digest.submitted}</b> submitted</span>
          <span><b className="text-emerald-600">{digest.resolved}</b> resolved</span>
          <span>avg triage <b className="text-foreground">{fmtDuration(digest.avg_triage_seconds)}</b></span>
          <span><b className="text-rose-600">{digest.open_high_priority}</b> open P1/P2 bugs</span>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-semibold text-muted-foreground">Type</span>
          <select value={typeF} onChange={(e) => setTypeF(e.target.value)} data-testid="board-filter-type" className="h-8 border border-input bg-background text-xs px-2">
            <option value="all">All</option><option value="bug">Bug</option><option value="comment">Comment</option><option value="inspiration">Inspiration</option><option value="agent_suggestion">Agent</option>
          </select>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-semibold text-muted-foreground">Assignee</span>
          <select value={assigneeF} onChange={(e) => setAssigneeF(e.target.value)} data-testid="board-filter-assignee" className="h-8 border border-input bg-background text-xs px-2">
            <option value="all">All</option><option value="Neo">Neo</option><option value="Emmy">Emmy</option><option value="Admin">Admin</option><option value="Tester">Tester</option>
          </select>
        </div>
        <div className="flex items-center gap-1.5 ml-auto" data-testid="wip-tuning">
          <span className="text-xs font-semibold text-muted-foreground">WIP limits</span>
          {["Triaging", "Fix Pending"].map((col) => (
            <label key={col} className="flex items-center gap-1 text-xs">
              {col}
              <input type="number" min="1" value={wipVal(col)} onChange={(e) => setWipDraft((d) => ({ ...(d || {}), [col]: Number(e.target.value) }))}
                data-testid={`wip-input-${col.replace(/\s+/g, "-")}`} className="w-14 h-8 border border-input bg-background text-xs px-1.5" />
            </label>
          ))}
          <Button onClick={saveWipLimits} disabled={saveWip.isPending} className="rounded-none h-8 px-3 bg-primary hover:opacity-90 text-xs" data-testid="wip-save">Save</Button>
        </div>
      </div>

      {shipped.length > 0 && (
        <div className="mb-4 border border-violet-400 bg-violet-500/5" data-testid="ship-queue">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-violet-400/50">
            <Rocket className="h-4 w-4 text-violet-600" />
            <span className="text-xs font-bold uppercase tracking-wider text-violet-600">Building Next</span>
            <span className="text-xs font-bold bg-violet-500/15 text-violet-600 px-1.5 rounded-full">{shipped.length}</span>
          </div>
          <div className="flex gap-2 overflow-x-auto p-2">
            {shipped.map((f) => {
              const meta = FEEDBACK_TYPE_META[f.type] || FEEDBACK_TYPE_META.bug;
              const Icon = meta.icon;
              return (
                <div key={f.id} onClick={() => setOpenId(f.id)} data-testid={`ship-card-${f.id}`}
                  className="min-w-[200px] max-w-[240px] bg-card border border-border rounded-lg p-3 cursor-pointer hover:border-foreground transition-colors">
                  <span className={`inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 ${meta.chip}`}><Icon className="h-3 w-3" /> {meta.label}</span>
                  <p className="text-xs leading-snug line-clamp-2 mt-1.5">{f.message}</p>
                  <div className="text-[10px] text-muted-foreground mt-1.5">@{f.assignee} · {f.status}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <p className="text-sm text-muted-foreground mb-3">Drag a card between columns to change its status · click a card to open details.</p>
      <div className="overflow-x-auto pb-4">
        <div className="flex gap-4 min-w-[1100px]">
          {BOARD_COLUMNS.map((status) => {
            const items = byStatus(status);
            const slug = status.replace(/\s+/g, "-");
            const limit = LIMITS[status];
            const over = limit != null && items.length > limit;
            return (
              <div key={status}
                onDragOver={(e) => { e.preventDefault(); setOverCol(status); }}
                onDragLeave={() => setOverCol((c) => (c === status ? null : c))}
                onDrop={(e) => onDrop(status, e)}
                data-testid={`kanban-col-${slug}`}
                className={`flex-1 min-w-[220px] bg-muted/30 border transition-shadow ${overCol === status ? "ring-2 ring-primary border-primary" : over ? "border-rose-400" : "border-border"}`}>
                <div className={`flex items-center justify-between px-3 py-2.5 border-b-2 bg-card ${COL_HEAD_CLS[status] || "border-border"}`}>
                  <div className="flex flex-col">
                    <span className="text-xs font-bold uppercase tracking-wider">{status}</span>
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 mt-0.5" data-testid={`kanban-ecu-${slug}`} title={status === "Deployed" ? "Total ECU deployed (what's live)" : "Sum of ECU for cards in this column"}>
                      <Zap className="h-3 w-3" /> {sumEcu(items)} ECU{status === "Deployed" ? " live" : ""}
                    </span>
                  </div>
                  <span className={`inline-flex items-center gap-1 text-xs font-bold px-1.5 rounded-full ${over ? "bg-rose-500/15 text-rose-600" : "bg-muted"}`} title={limit != null ? `WIP limit ${limit}` : undefined} data-testid={`kanban-count-${slug}`}>
                    {over && <AlertTriangle className="h-3 w-3" />}{items.length}{limit != null ? `/${limit}` : ""}
                  </span>
                </div>
                <div className="p-2 space-y-2 min-h-[120px]">
                  {items.map((f) => {
                    const meta = FEEDBACK_TYPE_META[f.type] || FEEDBACK_TYPE_META.bug;
                    const Icon = meta.icon;
                    return (
                      <div key={f.id} draggable
                        onDragStart={(e) => { setDragId(f.id); try { e.dataTransfer.setData("text/plain", f.id); e.dataTransfer.effectAllowed = "move"; } catch (err) {} }}
                        onDragEnd={() => { setDragId(null); setOverCol(null); }}
                        onClick={() => setOpenId(f.id)}
                        data-testid={`kanban-card-${f.id}`}
                        className={`bg-card border rounded-lg p-3 cursor-pointer hover:border-foreground transition-colors ${f.shipped ? "border-violet-400" : "border-border"} ${dragId === f.id ? "opacity-40" : ""}`}>
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <span className={`inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 ${meta.chip}`}>
                            <Icon className="h-3 w-3" /> {meta.label}
                          </span>
                          {f.shipped && <Rocket className="h-3 w-3 text-violet-600" />}
                          <span className="text-[10px] font-bold text-muted-foreground ml-auto">{f.priority}</span>
                          <span className={`text-[10px] font-bold ${SEVERITY_CLS[f.severity] || "text-muted-foreground"}`}>{f.severity}</span>
                        </div>
                        <p className="text-xs leading-snug line-clamp-3">{f.message}</p>
                        <div className="mt-2"><EcuBadge value={f.ecu} /></div>
                        <div className="flex items-center justify-between mt-2 text-[10px] text-muted-foreground">
                          <span className="font-semibold text-indigo-600">@{f.assignee}</span>
                          {f.type !== "bug" && f.status === "Future Backlog" ? (
                            <button onClick={(e) => { e.stopPropagation(); shipIt(f.id); }} data-testid={`kanban-ship-${f.id}`}
                              className="inline-flex items-center gap-1 font-bold text-violet-600 hover:text-white hover:bg-violet-600 border border-violet-400 rounded px-1.5 py-0.5 transition-colors">
                              <Rocket className="h-3 w-3" /> Ship it
                            </button>
                          ) : (
                            <span className="truncate max-w-[110px]">{f.user_email}</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {items.length === 0 && <p className="text-[11px] text-muted-foreground text-center py-4">Drop here</p>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {openId && <FeedbackDetailModal item={allRows.find((r) => r.id === openId)} onClose={() => setOpenId(null)} />}
    </div>
  );
}

export default function Admin() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const tab = searchParams.get("tab") || "tickets";
  const [failedOpen, setFailedOpen] = useState(false);

  const { data: stats } = useAdminStats();
  const { data: overview } = useBusinessOverview();

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-6xl mx-auto px-6 md:px-8 py-12">
        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-primary mb-2">Admin</p>
        <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
          <h1 className="font-display text-4xl font-extrabold tracking-tighter">Management Console</h1>
          <button onClick={() => navigate("/admin")} data-testid="new-admin-link"
            className="inline-flex items-center gap-2 text-sm font-semibold bg-primary text-primary-foreground px-4 h-10 hover:opacity-90 transition-opacity">
            <Sparkles className="h-4 w-4" /> New Admin
          </button>
        </div>

        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <Stat icon={Users} label="Users" value={stats.users} />
            <Stat icon={Crown} label="Members" value={stats.member_users} />
            <Stat icon={BookOpen} label="Courses" value={stats.courses} />
            <Stat icon={Ticket} label="Open tickets" value={stats.open_tickets} />
            <Stat icon={DollarSign} label="Revenue" value={`$${stats.revenue}`} />
          </div>
        )}

        {stats && (
          <div className="mb-10 border border-border bg-card overflow-hidden" data-testid="triage-health-card">
            <div className="flex items-center gap-2 px-5 py-3 border-b border-border" style={{ background: "linear-gradient(135deg, rgba(124,58,237,0.10), rgba(79,70,229,0.04))" }}>
              <FlaskConical className="h-4 w-4 text-indigo-600" />
              <span className="font-display font-bold tracking-tight">Triage Health</span>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Beta Companion</span>
            </div>
            <div className="grid sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-border">
              <div className="p-6" data-testid="triage-high-priority">
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Open High-Priority Bugs</div>
                <div className={`font-display text-4xl font-extrabold ${stats.open_high_priority_bugs > 0 ? "text-rose-600" : "text-emerald-600"}`}>
                  {stats.open_high_priority_bugs}
                </div>
                <div className="text-[11px] text-muted-foreground mt-1">P1 / P2 bugs not yet resolved</div>
              </div>
              <div className="p-6" data-testid="triage-avg-time">
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Average Triage Time</div>
                <div className="font-display text-4xl font-extrabold">{fmtDuration(stats.avg_triage_seconds)}</div>
                <div className="text-[11px] text-muted-foreground mt-1">From submission to resolved</div>
              </div>
              <div className="p-6" data-testid="triage-new">
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Awaiting Triage</div>
                <div className={`font-display text-4xl font-extrabold ${stats.new_feedback > 0 ? "text-amber-600" : "text-emerald-600"}`}>
                  {stats.new_feedback}
                </div>
                <div className="text-[11px] text-muted-foreground mt-1">New reports not yet triaged</div>
              </div>
            </div>
            {stats.feedback_types && (
              <div className="flex flex-wrap gap-2 px-6 py-4 border-t border-border" data-testid="triage-type-summary">
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-rose-500/10 text-rose-600"><Bug className="h-3.5 w-3.5" /> {stats.feedback_types.bug} Open Bugs</span>
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-sky-500/10 text-sky-600"><MessageSquare className="h-3.5 w-3.5" /> {stats.feedback_types.comment} Comments</span>
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-500/10 text-amber-600"><Lightbulb className="h-3.5 w-3.5" /> {stats.feedback_types.inspiration} Inspirations</span>
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-violet-500/10 text-violet-600"><Sparkles className="h-3.5 w-3.5" /> {stats.feedback_types.agent_suggestion} Agent Suggestions</span>
              </div>
            )}
          </div>
        )}

        {/* Business Overview */}
        {overview && (
          <section className="mb-12" data-testid="business-overview">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="h-5 w-5 text-primary" />
              <h2 className="font-display text-2xl font-bold tracking-tight">Business Overview</h2>
            </div>
            <div className="grid lg:grid-cols-3 gap-6 mb-6">
              <div className="lg:col-span-1 bg-foreground text-background p-8 text-grain" data-testid="gross-revenue-card">
                <DollarSign className="h-6 w-6 text-primary mb-4" />
                <div className="text-xs uppercase tracking-[0.2em] text-background/60 mb-2">Total Gross Revenue</div>
                <div className="font-display text-5xl font-extrabold tracking-tighter" data-testid="gross-revenue-value">
                  ${overview.total_gross_revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
              <div className="lg:col-span-2 grid sm:grid-cols-3 gap-4">
                <div className="border border-border bg-card p-6" data-testid="txn-successful">
                  <CheckCircle2 className="h-5 w-5 text-primary mb-3" />
                  <div className="font-display text-3xl font-extrabold">{overview.successful_count}</div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground mt-1">Successful</div>
                </div>
                <div className="border border-border bg-card p-6" data-testid="txn-pending">
                  <Clock className="h-5 w-5 text-muted-foreground mb-3" />
                  <div className="font-display text-3xl font-extrabold">{overview.pending_count}</div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground mt-1">Pending</div>
                </div>
                <div className={`border p-6 ${overview.failed_count > 0 ? "border-destructive bg-destructive/5" : "border-border bg-card"}`} data-testid="txn-failed">
                  <AlertTriangle className={`h-5 w-5 mb-3 ${overview.failed_count > 0 ? "text-destructive" : "text-muted-foreground"}`} />
                  <div className="font-display text-3xl font-extrabold">{overview.failed_count}</div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground mt-1">Failed</div>
                </div>
              </div>
            </div>

            <RevenueChart series={overview.revenue_series || []} />

            {/* Failed transaction alert (genuine payment failures only) */}
            {overview.failed_count > 0 ? (
              <div className="border border-destructive bg-destructive/5" data-testid="failed-alert">
                <button
                  type="button"
                  onClick={() => setFailedOpen((o) => !o)}
                  className="w-full flex items-center justify-between gap-2 px-5 py-3 text-destructive font-semibold hover:bg-destructive/10 transition-colors"
                  data-testid="failed-alert-toggle"
                >
                  <span className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" /> {overview.failed_count} failed payment{overview.failed_count > 1 ? "s" : ""}
                  </span>
                  {failedOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>
                {failedOpen && (
                  <div className="border-t border-destructive/30">
                    <Table>
                      <TableHeader><TableRow><TableHead>User</TableHead><TableHead>Amount</TableHead><TableHead>Package</TableHead><TableHead>Status</TableHead><TableHead>When</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {overview.failed_transactions.map((t) => (
                          <TableRow key={t.session_id} data-testid={`failed-txn-${t.session_id}`}>
                            <TableCell className="font-medium">{t.user_email}</TableCell>
                            <TableCell>${Number(t.amount).toFixed(2)}</TableCell>
                            <TableCell>{t.package_id}</TableCell>
                            <TableCell><span className="text-destructive font-semibold">{t.payment_status || t.status}</span></TableCell>
                            <TableCell className="text-muted-foreground text-xs">{t.created_at ? new Date(t.created_at).toLocaleString() : "—"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            ) : (
              <div className="border border-border bg-card p-5 flex items-center gap-2 text-sm text-muted-foreground" data-testid="failed-alert-empty">
                <CheckCircle2 className="h-4 w-4 text-primary" /> No failed payments — all charges are healthy.
              </div>
            )}
            {overview.abandoned_count > 0 && (
              <p className="mt-2 text-xs text-muted-foreground" data-testid="abandoned-note">
                {overview.abandoned_count} checkout{overview.abandoned_count > 1 ? "s were" : " was"} started but never completed (abandoned — not counted as failures).
              </p>
            )}
          </section>
        )}

        <Tabs value={tab} onValueChange={(v) => setSearchParams({ tab: v })}>
          <TabsList className="rounded-none flex-wrap h-auto">
            <TabsTrigger value="tickets" className="rounded-none" data-testid="tab-tickets">Tickets</TabsTrigger>
            <TabsTrigger value="users" className="rounded-none" data-testid="tab-users">Users</TabsTrigger>
            <TabsTrigger value="courses" className="rounded-none" data-testid="tab-courses">Courses</TabsTrigger>
            <TabsTrigger value="transactions" className="rounded-none" data-testid="tab-transactions">Transactions</TabsTrigger>
            <TabsTrigger value="moderation" className="rounded-none" data-testid="tab-moderation">Safety Logs</TabsTrigger>
            <TabsTrigger value="feedback" className="rounded-none relative" data-testid="tab-feedback">
              Feedback Lab
              {stats?.new_feedback > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-destructive rounded-full animate-pulse" data-testid="feedback-new-badge">
                  {stats.new_feedback}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="promos" className="rounded-none" data-testid="tab-promos">Promos</TabsTrigger>
            <TabsTrigger value="board" className="rounded-none" data-testid="tab-board">Backlog Board</TabsTrigger>
            <TabsTrigger value="uat" className="rounded-none" data-testid="tab-uat">UAT Dashboard</TabsTrigger>
            <TabsTrigger value="bulk" className="rounded-none" data-testid="tab-bulk">Bulk Generations</TabsTrigger>
            <TabsTrigger value="playlist" className="rounded-none" data-testid="tab-playlist">Public Playlist</TabsTrigger>
            <TabsTrigger value="landing" className="rounded-none" data-testid="tab-landing">Landing Content</TabsTrigger>
            <TabsTrigger value="settings" className="rounded-none" data-testid="tab-settings">Pricing Settings</TabsTrigger>
            <TabsTrigger value="alerts" className="rounded-none" data-testid="tab-alerts">Alerts Log</TabsTrigger>
            <TabsTrigger value="strategy" className="rounded-none" data-testid="tab-strategy">Strategy Room</TabsTrigger>
          </TabsList>

          <TabsContent value="tickets" className="mt-6 space-y-4" data-testid="admin-tickets">
            <SupportTicketsPanel />
          </TabsContent>

          <TabsContent value="strategy" className="mt-6" data-testid="admin-strategy">
            <div className="border border-border bg-card p-6">
              <h3 className="font-display text-xl font-bold mb-1">Strategy Room</h3>
              <p className="text-sm text-muted-foreground mb-5">Internal strategy docs — admin only. Not linked anywhere public.</p>
              <div className="grid sm:grid-cols-3 gap-4">
                {[
                  { href: "/learn-me-tour.html", title: "Product Tour", desc: "Guided walkthrough of the product." },
                  { href: "/learn-me-economics.html", title: "Unit Economics", desc: "Cost, pricing & margin model." },
                  { href: "/learn-me-risk.html", title: "Risk Analysis", desc: "Key risks & mitigations." },
                ].map((d) => (
                  <a key={d.href} href={d.href} target="_blank" rel="noopener noreferrer"
                    data-testid={`strategy-link-${d.href.replace(/[^a-z]/gi, "")}`}
                    className="block border border-border p-5 hover:border-primary transition-colors group">
                    <div className="flex items-center gap-2 mb-2 font-semibold">
                      <Layers className="h-4 w-4 text-primary" /> {d.title}
                    </div>
                    <p className="text-sm text-muted-foreground">{d.desc}</p>
                    <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary opacity-0 group-hover:opacity-100 transition-opacity">Open <ArrowRight className="h-3 w-3" /></span>
                  </a>
                ))}
              </div>
            </div>
            <SetupDatabasePanel />
            <LaunchResetPanel />
          </TabsContent>

          <TabsContent value="users" className="mt-6" data-testid="admin-users">
            <UsersPanel />
          </TabsContent>

          <TabsContent value="courses" className="mt-6" data-testid="admin-courses">
            <CoursesPanel />
          </TabsContent>

          <TabsContent value="bulk" className="mt-6" data-testid="admin-bulk-tab">
            <BulkJobs />
          </TabsContent>

          <TabsContent value="playlist" className="mt-6" data-testid="admin-playlist-tab">
            <PublicPlaylistManager />
          </TabsContent>

          <TabsContent value="landing" className="mt-6" data-testid="admin-landing-tab">
            <LandingContentManager />
          </TabsContent>

          <TabsContent value="settings" className="mt-6" data-testid="admin-settings-tab">
            <PricingSettings />
          </TabsContent>

          <TabsContent value="alerts" className="mt-6" data-testid="admin-alerts-tab">
            <AlertsLog />
          </TabsContent>

          <TabsContent value="transactions" className="mt-6" data-testid="admin-transactions-tab">
            <TransactionsPanel />
          </TabsContent>

          <TabsContent value="moderation" className="mt-6" data-testid="admin-moderation-tab">
            <ModerationLog />
          </TabsContent>

          <TabsContent value="feedback" className="mt-6" data-testid="admin-feedback-tab">
            <FeedbackLab />
          </TabsContent>

          <TabsContent value="promos" className="mt-6" data-testid="admin-promos-tab">
            <PromosManager />
          </TabsContent>

          <TabsContent value="board" className="mt-6" data-testid="admin-board-tab">
            <KanbanBoard />
          </TabsContent>

          <TabsContent value="uat" className="mt-6" data-testid="admin-uat-tab">
            <UATDashboard />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}


function SetupDatabasePanel() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const run = async () => {
    setBusy(true);
    try {
      const r = await adminService.setupDatabase();
      setResult(r);
      toast.success("Database indexes initialized (incl. rate-limit TTL).");
    } catch (e) { toast.error(e?.response?.data?.detail || "Setup failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-6 border border-border bg-card p-6" data-testid="setup-database-panel">
      <div className="flex items-center gap-2 mb-1 font-bold">
        <Database className="h-5 w-5 text-primary" /> Setup Database
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        One-time, idempotent index initialization. Creates all MongoDB indexes including the
        rate-limit <strong>TTL</strong> index (not created automatically at startup). Safe to re-run.
      </p>
      <Button onClick={run} disabled={busy} variant="outline" className="rounded-none h-9" data-testid="setup-database-button">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Database className="h-4 w-4" />} Initialize Indexes
      </Button>
      {result && (
        <pre className="mt-4 p-3 bg-black/60 text-xs text-emerald-300 overflow-auto rounded" data-testid="setup-database-result">
{JSON.stringify(result, null, 2)}
        </pre>
      )}
    </div>
  );
}

function LaunchResetPanel() {
  const PHRASE = "RESET FOR LAUNCH";
  const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const errMsg = (e) => e?.response?.data?.detail || "Request failed";

  const runDry = async () => {
    setBusy(true);
    try { setResult(await adminService.launchReset({ confirm: PHRASE, dry_run: true })); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const runReal = async () => {
    if (phrase.trim() !== PHRASE) { toast.error(`Type exactly: ${PHRASE}`); return; }
    setBusy(true);
    try {
      const r = await adminService.launchReset({ confirm: phrase.trim(), dry_run: false });
      setResult(r); setPhrase("");
      if (r.verified_pass) toast.success(`Done — ${r.after.users} users kept, collections cleared (backup ${r.backup_suffix}).`);
      else toast.warning("Completed, but verification did not pass — review counts below.");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="mt-6 border border-red-500/40 bg-red-500/5 p-6" data-testid="launch-reset-panel">
      <div className="flex items-center gap-2 mb-1 text-red-500 font-bold">
        <AlertTriangle className="h-5 w-5" /> Danger Zone — Launch Reset
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Backs up, then wipes <strong>courses, feedback, waitlist, payment_transactions</strong> and deletes ALL users
        except the 4 launch whitelist accounts. <strong>settings</strong> (branding, pricing, name) is never touched.
        This is irreversible except via the auto-backup. Run <em>Preview</em> first.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={runDry} disabled={busy} variant="outline" className="rounded-none h-9" data-testid="launch-reset-preview">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />} Preview (dry-run)
        </Button>
        <Input value={phrase} onChange={(e) => setPhrase(e.target.value)}
          placeholder={`Type "${PHRASE}"`} className="h-9 rounded-none w-56" data-testid="launch-reset-phrase" />
        <Button onClick={runReal} disabled={busy || phrase.trim() !== PHRASE}
          className="rounded-none h-9 bg-red-600 hover:bg-red-700 text-white" data-testid="launch-reset-execute">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Execute Reset
        </Button>
      </div>
      {result && (
        <pre className="mt-4 p-3 bg-black/60 text-xs text-emerald-300 overflow-auto rounded" data-testid="launch-reset-result">
{JSON.stringify(result, null, 2)}
        </pre>
      )}
    </div>
  );
}


function UATDashboard() {
  const { data, isLoading } = useAdminFeedback({});
  const update = useUpdateFeedback({ onError: (e) => toast.error(apiError(e)) });
  const items = (data?.feedback || []).filter((i) => i.status === "UAT Ready" && !i.archived);
  const overLimit = items.length > 5;
  return (
    <div className="space-y-4" data-testid="uat-dashboard">
      {overLimit && (
        <div className="border border-amber-500 bg-amber-500/10 text-amber-700 rounded-xl p-3 text-sm font-semibold flex items-center gap-2" data-testid="uat-wip-warning">
          <AlertTriangle className="h-4 w-4" /> WIP limit exceeded — {items.length} items awaiting QA (recommended max 5). Clear the queue before sending more to UAT.
        </div>
      )}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-display text-xl font-bold flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-fuchsia-600" /> UAT Dashboard
          </h3>
          <p className="text-sm text-muted-foreground">Fixes awaiting Emmy's final QA review before production.</p>
        </div>
        <span className="text-xs font-bold px-2.5 py-1 border border-fuchsia-500 text-fuchsia-600 bg-fuchsia-500/10" data-testid="uat-count">{items.length} awaiting QA</span>
      </div>
      <div>
        <button onClick={async () => { try { const r = await adminService.uatDigest(); toast.success(`Digest emailed to ${r.sent_to} (${r.count} items)`); } catch (e) { toast.error(apiError(e)); } }}
          data-testid="uat-email-digest"
          className="inline-flex items-center gap-1.5 text-xs font-bold px-3 h-8 border border-fuchsia-500 text-fuchsia-600 hover:bg-fuchsia-500/10 rounded">
          <Sparkles className="h-3.5 w-3.5" /> Email digest to Emmy
        </button>
      </div>

      {isLoading ? (
        <div className="py-10 text-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
      ) : items.length === 0 ? (
        <div className="py-12 text-center text-muted-foreground border border-dashed border-border rounded-xl" data-testid="uat-empty">
          Nothing in UAT right now. Finished fixes will land here automatically.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((it) => {
            const meta = FEEDBACK_TYPE_META[it.type] || FEEDBACK_TYPE_META.bug;
            return (
              <div key={it.id} className="border border-fuchsia-500/30 bg-card rounded-xl p-4" data-testid={`uat-card-${it.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold px-2 py-1 ${meta.chip}`}>{meta.label}</span>
                  <div className="flex items-center gap-2">
                    <EcuBadge value={it.ecu} />
                    <span className="text-[10px] text-muted-foreground">{it.uat_ready_at ? new Date(it.uat_ready_at).toLocaleString() : ""}</span>
                  </div>
                </div>
                <p className="text-sm mt-2 whitespace-pre-wrap break-words">{it.message}</p>
                <div className="grid md:grid-cols-2 gap-3 mt-3">
                  <div className="bg-background border border-border rounded p-2.5">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-sky-600 mb-1">Impacted Files</div>
                    <pre className="text-xs whitespace-pre-wrap break-words text-muted-foreground" data-testid={`uat-card-impacted-${it.id}`}>{it.impacted_files || "—"}</pre>
                  </div>
                  <div className="bg-background border border-border rounded p-2.5">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-600 mb-1">Functional Changes</div>
                    <pre className="text-xs whitespace-pre-wrap break-words text-muted-foreground" data-testid={`uat-card-changes-${it.id}`}>{it.functional_changes || "—"}</pre>
                  </div>
                </div>
                <UATReviewControls it={it} update={update} />
                <div className="mt-3 text-[11px] text-muted-foreground">From: {it.user_email} · Assignee: {it.assignee}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function UATReviewControls({ it, update }) {
  const [note, setNote] = useState(it.reviewer_note || "");
  const pass = () => update.mutate({ id: it.id, status: "Resolved", reviewer_note: note });
  const fail = () => {
    if (!note.trim()) { toast.error("Add a reviewer note before failing — context is required."); return; }
    update.mutate({ id: it.id, status: "Fix Pending", assignee: "Neo", reviewer_note: note });
  };
  const history = it.uat_history || [];
  const HCLR = { pass: "text-emerald-600", fail: "text-rose-600", handoff: "text-fuchsia-600" };
  return (
    <div className="mt-3 border-t border-border pt-3 space-y-2" data-testid={`uat-review-${it.id}`}>
      {history.length > 0 && (
        <div className="text-[11px] space-y-1" data-testid={`uat-history-${it.id}`}>
          <div className="uppercase tracking-wider font-bold text-muted-foreground">History</div>
          {history.map((h, i) => (
            <div key={i} className="flex gap-2">
              <span className={`font-bold uppercase ${HCLR[h.action] || ""}`}>{h.action}</span>
              <span className="text-muted-foreground">{h.by} · {h.at ? new Date(h.at).toLocaleString() : ""}{h.note ? ` — ${h.note}` : ""}</span>
            </div>
          ))}
        </div>
      )}
      <Label className="text-xs">Reviewer note (Emmy) — required to Fail</Label>
      <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid={`uat-note-${it.id}`}
        className="w-full border border-border bg-background p-2 text-xs rounded outline-none focus:border-fuchsia-500"
        placeholder="QA comments — required context for pass/fail" />
      <div className="flex items-center gap-2">
        <button onClick={pass} disabled={update.isPending} data-testid={`uat-pass-${it.id}`}
          className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg font-bold text-white bg-emerald-600 hover:bg-emerald-700">
          <Check className="h-4 w-4" /> Pass → Resolved
        </button>
        <button onClick={fail} disabled={update.isPending} data-testid={`uat-fail-${it.id}`}
          className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg font-bold text-white bg-rose-600 hover:bg-rose-700">
          <AlertTriangle className="h-4 w-4" /> Fail → Fix Pending
        </button>
      </div>
    </div>
  );
}

